<template>
  <div class="weather-map-container">
    <!-- Mobile-friendly hamburger menu -->
    <div class="map-controls" :class="{ 'controls-open': controlsOpen }">

      <button class="controls-toggle" @click="controlsOpen = !controlsOpen">
        <svg v-if="controlsOpen" xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
          <path d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8 2.146 2.854Z"/>
        </svg>
        <svg v-else xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
          <path fill-rule="evenodd" d="M2.5 12a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5zm0-4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5zm0-4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5z"/>
        </svg>
      </button>
      
      <div class="controls-panel">

        <!-- Weather Layers -->
        <div class="control-group">
          <div class="d-flex justify-content-between align-items-center">
            <label class="fw-bold mb-0">Layers</label>
            <button @click="showAddLayerModal = true" class="btn btn-sm btn-outline-primary py-0 px-1">
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16">
                <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z"/>
              </svg>
            </button>
          </div>
          <div class="layer-list">
            <!-- API Key warning -->
            <div v-if="!hasValidApiKey && activeLayers.length > 0" class="api-key-warning">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"/>
              </svg>
              <span>No API key - tiles won't load</span>
            </div>
            <div v-for="(layer, index) in activeLayers" :key="layer.id" class="layer-item-compact">
              <div class="d-flex align-items-center gap-1">
                <input type="checkbox" class="form-check-input m-0" v-model="layer.visible" @change="toggleLayerVisibility(layer)">
                <span class="layer-label flex-grow-1">
                  {{ getElementDisplayName(layer.element) }}
                  <span v-if="layer.model !== 'default'" class="model-badge">{{ getModelDisplayName(layer.model) }}</span>
                </span>
                <input v-if="layer.visible" type="range" v-model.number="layer.opacity" min="0" max="1" step="0.1"
                       @input="updateLayerOpacity(layer)" class="form-range form-range-sm opacity-slider">
                <span v-if="layerErrors[layer.id]" class="layer-error-badge" :title="layerErrors[layer.id] + ' tile errors'">!</span>
                <button @click="removeLayer(index)" class="btn btn-link text-danger p-0">
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16">
                    <path d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8 2.146 2.854Z"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Overlays -->
        <div class="control-group compact">
          <label class="fw-bold">Overlays</label>
          <div class="d-flex gap-3 align-items-center flex-wrap">
            <div class="form-check form-switch mb-0">
              <input type="checkbox" class="form-check-input" id="cities-toggle" v-model="showGeoJson" @change="toggleGeoJsonLayer">
              <label class="form-check-label small" for="cities-toggle">Wx</label>
            </div>
            <select v-if="showGeoJson" v-model="geoJsonElement" @change="refreshGeoJsonTiles" class="form-control form-control-sm" style="width: auto; min-width: 80px;">
              <option value="temp">Temp</option>
              <option value="precip">Precip</option>
              <option value="wind">Wind</option>
            </select>
            <div class="form-check form-switch mb-0">
              <input type="checkbox" class="form-check-input" id="labels-toggle" v-model="showLabels" @change="toggleLabels">
              <label class="form-check-label small" for="labels-toggle">Labels</label>
            </div>
            <div class="form-check form-switch mb-0">
              <input type="checkbox" class="form-check-input" id="tilegrid-toggle" v-model="showTileGrid" @change="toggleTileGrid">
              <label class="form-check-label small" for="tilegrid-toggle">Tiles</label>
            </div>
          </div>
        </div>
        
        <!-- Markers section -->
        <div class="control-group compact">
          <div class="d-flex justify-content-between align-items-center">
            <label class="fw-bold mb-0">Markers</label>
            <button @click="goToUserLocation" class="btn btn-sm btn-outline-secondary py-0 px-1" title="Go to my location">
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16">
                <path d="M8 16s6-5.686 6-10A6 6 0 0 0 2 6c0 4.314 6 10 6 10zm0-7a3 3 0 1 1 0-6 3 3 0 0 1 0 6z"/>
              </svg>
            </button>
          </div>
          <div class="d-flex gap-1 align-items-center">
            <input type="text" v-model="newMarkerLatLng" @keyup.enter="addMarkerFromInput" 
                   class="form-control form-control-sm flex-grow-1" placeholder="latitude, longitude">
            <button @click="addMarkerFromInput" class="btn btn-sm btn-outline-primary py-0 px-1" title="Add marker">
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16">
                <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z"/>
              </svg>
            </button>
            <button @click="addMarkerFromGPS" class="btn btn-sm btn-outline-secondary py-0 px-1" title="Add from GPS">
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16">
                <path d="M8 0a.5.5 0 0 1 .5.5v.518A7.001 7.001 0 0 1 14.982 7.5h.518a.5.5 0 0 1 0 1h-.518A7.001 7.001 0 0 1 8.5 14.982v.518a.5.5 0 0 1-1 0v-.518A7.001 7.001 0 0 1 1.018 8.5H.5a.5.5 0 0 1 0-1h.518A7.001 7.001 0 0 1 7.5 1.018V.5A.5.5 0 0 1 8 0zM8 3a5 5 0 1 0 0 10A5 5 0 0 0 8 3z"/>
              </svg>
            </button>
          </div>
          <div v-if="userMarkers.length" class="marker-list">
            <div v-for="(m, i) in userMarkers" :key="m.id" class="marker-item" @click="panToMarker(m)" title="Click to center on marker">
              <span class="marker-dot" :style="{ background: m.color }"></span>
              <span class="marker-coords">{{ m.lat.toFixed(3) }}, {{ m.lng.toFixed(3) }}</span>
              <button @click.stop="removeMarker(i)" class="marker-remove-btn" title="Remove marker">×</button>
            </div>
          </div>
        </div>

        <!-- Advanced section - collapsed by default, only visible with URL param showadvanced=true -->
        <div v-if="advancedEnabled" class="control-group compact">
          <div class="advanced-header" @click="showAdvanced = !showAdvanced">
            <label class="fw-bold mb-0" style="cursor: pointer;">Advanced</label>
            <svg v-if="showAdvanced" xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16">
              <path fill-rule="evenodd" d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z"/>
            </svg>
            <svg v-else xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16">
              <path fill-rule="evenodd" d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708z"/>
            </svg>
          </div>
          <div v-if="showAdvanced" class="advanced-content">
            <div class="control-group compact">
              <label>Units</label>
              <select v-model="unitGroup" class="form-control form-control-sm">
                <option value="us">US</option>
                <option value="metric">Metric</option>
              </select>
            </div>
            <div class="mb-2">
              <label >Server</label>
              <select v-model="baseApiUrl" class="form-control form-control-sm">
                 <option value="https://maps.visualcrossing.com">Maps</option>
                  <option value="http://localhost:8080">Localhost</option>
                 <!--
                <option value="https://weather5.visualcrossing.com">Weather5</option>
                <option value="http://localhost:8080">Localhost</option>
                -->
              </select>
            </div>
            <!-- Base map -->
            <div class="control-group compact">
              <label>Base Map</label>
              <select v-model="selectedBaseMap" @change="changeBaseMap" class="form-control form-control-sm">
                <option value="carto-light">Light</option>
                <option value="carto-dark">Dark</option>
                <!--<option value="osm">OpenStreetMap</option>-->
                <option value="vcosm1">VC OSM 1</option>
                <option value="vcosm2">VC OSM 2</option>
              </select>
            </div>
            <div class="form-check mb-2">
              <input type="checkbox" class="form-check-input" id="strict-toggle" v-model="strictMode" @change="saveSettings">
              <label class="form-check-label" for="strict-toggle">Strict Mode</label>
            </div>
            <div class="d-flex align-items-center gap-2">
              <small class="text-muted">{{ viewInfo.centerPair }}</small>
              <small class="text-muted">z{{ viewInfo.zoom }}</small>
              <button @click="copyCenterToClipboard" class="btn btn-sm btn-link p-0" title="Copy coordinates">
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16">
                  <path d="M4 1.5H3a2 2 0 0 0-2 2V14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V3.5a2 2 0 0 0-2-2h-1v1h1a1 1 0 0 1 1 1V14a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V3.5a1 1 0 0 1 1-1h1v-1z"/>
                  <path d="M9.5 1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-3a.5.5 0 0 1-.5-.5v-1a.5.5 0 0 1 .5-.5h3zm-3-1A1.5 1.5 0 0 0 5 1.5v1A1.5 1.5 0 0 0 6.5 4h3A1.5 1.5 0 0 0 11 2.5v-1A1.5 1.5 0 0 0 9.5 0h-3z"/>
                </svg>
              </button>
            </div>
          </div>
        </div>

        <!-- Integration section - shows URL patterns for layers -->
        <div class="control-group compact">
          <div class="advanced-header" @click="showIntegration = !showIntegration">
            <label class="fw-bold mb-0" style="cursor: pointer;">API Integration</label>
            <svg v-if="showIntegration" xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16">
              <path fill-rule="evenodd" d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z"/>
            </svg>
            <svg v-else xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16">
              <path fill-rule="evenodd" d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708z"/>
            </svg>
          </div>
          <div v-if="showIntegration" class="advanced-content">
            <p class="small text-muted mb-2">Use these URL patterns to integrate weather tiles into your own mapping application.</p>
            
            <!-- API Key section - always visible -->
            <div class="control-group compact mb-3 p-2 bg-light rounded">
              <label class="small fw-bold">API Key</label>
              <div class="d-flex align-items-center gap-1">
                <input 
                  type="text" 
                  v-model="displayApiKey" 
                  class="form-control form-control-sm" 
                  placeholder="Enter your API key"
                >
                <button 
                  v-if="apiKeyOverride" 
                  @click="clearApiKeyOverride" 
                  class="btn btn-sm btn-outline-secondary py-0 px-1" 
                  title="Clear manual key"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16">
                    <path d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8 2.146 2.854Z"/>
                  </svg>
                </button>
              </div>
              <small class="text-muted">
                <span v-if="hasSession && !apiKeyOverride">Using key from session</span>
                <span v-else-if="apiKeyOverride">Using custom key</span>
                <span v-else>Enter your API key or sign in</span>
              </small>
            </div>
            
            <div v-if="activeLayers.length === 0" class="text-muted small">
              <em>Add a weather layer to see its URL pattern.</em>
            </div>
            
            <div v-for="layer in activeLayers" :key="'url-' + layer.id" class="url-pattern-item mb-3">
              <div class="d-flex justify-content-between align-items-center mb-1">
                <strong class="small">{{ getElementDisplayName(layer.element) }}</strong>
              </div>
              <div class="mb-2">
                <div class="d-flex justify-content-between align-items-center">
                  <label class="small text-muted mb-0">Tile URL:</label>
                  <button @click="copyUrlPattern(layer, 'tile')" class="btn btn-sm btn-outline-secondary py-0 px-1" title="Copy Tile URL">
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16">
                      <path d="M4 1.5H3a2 2 0 0 0-2 2V14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V3.5a2 2 0 0 0-2-2h-1v1h1a1 1 0 0 1 1 1V14a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V3.5a1 1 0 0 1 1-1h1v-1z"/>
                      <path d="M9.5 1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-3a.5.5 0 0 1-.5-.5v-1a.5.5 0 0 1 .5-.5h3zm-3-1A1.5 1.5 0 0 0 5 1.5v1A1.5 1.5 0 0 0 6.5 4h3A1.5 1.5 0 0 0 11 2.5v-1A1.5 1.5 0 0 0 9.5 0h-3z"/>
                    </svg>
                  </button>
                </div>
                <code class="url-pattern-code">{{ getLayerUrlPattern(layer) }}</code>
              </div>
              <div>
                <div class="d-flex justify-content-between align-items-center">
                  <label class="small text-muted mb-0">Legend URL:</label>
                  <button @click="copyUrlPattern(layer, 'legend')" class="btn btn-sm btn-outline-secondary py-0 px-1" title="Copy Legend URL">
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16">
                      <path d="M4 1.5H3a2 2 0 0 0-2 2V14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V3.5a2 2 0 0 0-2-2h-1v1h1a1 1 0 0 1 1 1V14a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V3.5a1 1 0 0 1 1-1h1v-1z"/>
                      <path d="M9.5 1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-3a.5.5 0 0 1-.5-.5v-1a.5.5 0 0 1 .5-.5h3zm-3-1A1.5 1.5 0 0 0 5 1.5v1A1.5 1.5 0 0 0 6.5 4h3A1.5 1.5 0 0 0 11 2.5v-1A1.5 1.5 0 0 0 9.5 0h-3z"/>
                    </svg>
                  </button>
                </div>
                <code class="url-pattern-code">{{ getLegendUrlPattern(layer) }}</code>
              </div>
            </div>

            <div class="mt-2 pt-2 border-top">
              <strong class="small d-block mb-1">URL Parameters:</strong>
              <ul class="small text-muted mb-0 ps-3">
                <li><code>{z}/{x}/{y}</code> - Tile coordinates</li>
                <li><code>time</code> - ISO datetime or "now"</li>
                <li><code>unitGroup</code> - "us" or "metric"</li>
                <li><code>source</code> - Model/data source (optional)</li>
                <li><code>strict</code> - Strict time matching</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Add Layer Modal -->
    <div v-if="showAddLayerModal" class="add-layer-modal" @click.self="showAddLayerModal = false">
      <div class="modal-content-custom">
        <h6>Add Weather Layer</h6>
        
        <div class="mb-3">
          <label class="form-label small">Model / Source</label>
          <select v-model="newLayerModel" @change="onModelChange" class="form-control form-control-sm">
            <option v-for="(config, modelId) in modelOptions" :key="modelId" :value="modelId">
              {{ getModelDisplayName(modelId) }}
            </option>
          </select>
        </div>
        
        <div class="mb-3">
          <label class="form-label small">Element</label>
          <select v-model="newLayerElement" class="form-control form-control-sm">
            <option v-for="element in availableElements" :key="element" :value="element">
              {{ getElementDisplayName(element) }}
            </option>
          </select>
        </div>
        
        <div class="d-flex gap-2">
          <button @click="showAddLayerModal = false" class="btn btn-secondary btn-sm flex-fill">Cancel</button>
          <button @click="addLayer" class="btn btn-primary btn-sm flex-fill" :disabled="!newLayerElement">Add</button>
        </div>
      </div>
    </div>

    <!-- Map container -->
    <div id="map" class="map-view">
      <!-- Animation Control Bar (compact, near time display) -->
      <div class="animation-control-bar" :class="{ 'with-timeline': showTimelinePanel && animationTimeKeys.length > 0 }" @click.stop @dblclick.stop @mousedown.stop>
        <div class="control-bar-main">
          <!-- Time display -->
          <div class="time-display-inline" @click="showDateTimePicker = true" title="Click to set time">
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16">
              <path d="M8 3.5a.5.5 0 0 0-1 0V9a.5.5 0 0 0 .252.434l3.5 2a.5.5 0 0 0 .496-.868L8 8.71V3.5z"/>
              <path d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zm7-8A7 7 0 1 1 1 8a7 7 0 0 1 14 0z"/>
            </svg>
            <span class="time-text">{{ formattedTime }}</span>
          </div>
          
          <!-- Reset to now button (separate from date picker) -->
          <button @click="resetToNow" class="anim-btn reset-now-btn-separate" title="Reset to current time">
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16">
              <path fill-rule="evenodd" d="M8 3a5 5 0 1 0 4.546 2.914.5.5 0 0 1 .908-.417A6 6 0 1 1 8 2v1z"/>
              <path d="M8 4.466V.534a.25.25 0 0 1 .41-.192l2.36 1.966c.12.1.12.284 0 .384L8.41 4.658A.25.25 0 0 1 8 4.466z"/>
            </svg>
          </button>
          
          <!-- Playback buttons -->
          <div class="playback-buttons">
            <button @click="decrementTime" class="anim-btn" title="Previous frame">
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16">
                <path d="M.5 3.5A.5.5 0 0 1 1 4v3.248l6.267-3.636c.52-.302 1.233.043 1.233.696v2.94l6.267-3.636c.52-.302 1.233.043 1.233.696v7.384c0 .653-.713.998-1.233.696L8.5 8.752v2.94c0 .653-.713.998-1.233.696L1 8.752V12a.5.5 0 0 1-1 0V4a.5.5 0 0 1 .5-.5z"/>
              </svg>
            </button>
            <button @click="toggleMoviePlay" class="anim-btn play-btn" :disabled="isPreloadingFrames" :title="isPlaying ? 'Pause' : 'Play'">
              <svg v-if="isPreloadingFrames" xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" class="spin" viewBox="0 0 16 16">
                <path d="M8 3a5 5 0 1 0 4.546 2.914.5.5 0 0 1 .908-.417A6 6 0 1 1 8 2z"/>
                <path d="M8 4.466V.534a.25.25 0 0 1 .41-.192l2.36 1.966c.12.1.12.284 0 .384L8.41 4.658A.25.25 0 0 1 8 4.466z"/>
              </svg>
              <svg v-else-if="isPlaying" xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                <path d="M5.5 3.5A1.5 1.5 0 0 1 7 5v6a1.5 1.5 0 0 1-3 0V5a1.5 1.5 0 0 1 1.5-1.5zm5 0A1.5 1.5 0 0 1 12 5v6a1.5 1.5 0 0 1-3 0V5a1.5 1.5 0 0 1 1.5-1.5z"/>
              </svg>
              <svg v-else xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                <path d="m11.596 8.697-6.363 3.692c-.54.313-1.233-.066-1.233-.697V4.308c0-.63.692-1.01 1.233-.696l6.363 3.692a.802.802 0 0 1 0 1.393z"/>
              </svg>
            </button>
            <button @click="incrementTime" class="anim-btn" title="Next frame">
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16">
                <path d="M15.5 3.5a.5.5 0 0 1 .5.5v8a.5.5 0 0 1-1 0V8.752l-6.267 3.636c-.52.302-1.233-.043-1.233-.696v-2.94l-6.267 3.636C.713 12.69 0 12.345 0 11.692V4.308c0-.653.713-.998 1.233-.696L7.5 7.248v-2.94c0-.653.713-.998 1.233-.696L15 7.248V4a.5.5 0 0 1 .5-.5z"/>
              </svg>
            </button>
          </div>
          
          <!-- Expand timeline toggle -->
          <button @click="toggleTimelinePanel" class="anim-btn expand-btn" :class="{ active: showTimelinePanel }" title="Show timeline">
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16">
              <path fill-rule="evenodd" d="M1 8a.5.5 0 0 1 .5-.5h13a.5.5 0 0 1 0 1h-13A.5.5 0 0 1 1 8zM1 4a.5.5 0 0 1 .5-.5h13a.5.5 0 0 1 0 1h-13A.5.5 0 0 1 1 4zm0 8a.5.5 0 0 1 .5-.5h13a.5.5 0 0 1 0 1h-13A.5.5 0 0 1 1 12z"/>
            </svg>
          </button>
          
          <!-- Settings toggle -->
          <button @click="showAnimationSettings = !showAnimationSettings" class="anim-btn settings-btn" :class="{ active: showAnimationSettings }" title="Animation settings">
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16">
              <path d="M8 4.754a3.246 3.246 0 1 0 0 6.492 3.246 3.246 0 0 0 0-6.492zM5.754 8a2.246 2.246 0 1 1 4.492 0 2.246 2.246 0 0 1-4.492 0z"/>
              <path d="M9.796 1.343c-.527-1.79-3.065-1.79-3.592 0l-.094.319a.873.873 0 0 1-1.255.52l-.292-.16c-1.64-.892-3.433.902-2.54 2.541l.159.292a.873.873 0 0 1-.52 1.255l-.319.094c-1.79.527-1.79 3.065 0 3.592l.319.094a.873.873 0 0 1 .52 1.255l-.16.292c-.892 1.64.901 3.434 2.541 2.54l.292-.159a.873.873 0 0 1 1.255.52l.094.319c.527 1.79 3.065 1.79 3.592 0l.094-.319a.873.873 0 0 1 1.255-.52l.292.16c1.64.893 3.434-.902 2.54-2.541l-.159-.292a.873.873 0 0 1 .52-1.255l.319-.094c1.79-.527 1.79-3.065 0-3.592l-.319-.094a.873.873 0 0 1-.52-1.255l.16-.292c.893-1.64-.902-3.433-2.541-2.54l-.292.159a.873.873 0 0 1-1.255-.52l-.094-.319zm-2.633.283c.246-.835 1.428-.835 1.674 0l.094.319a1.873 1.873 0 0 0 2.693 1.115l.291-.16c.764-.415 1.6.42 1.184 1.185l-.159.292a1.873 1.873 0 0 0 1.116 2.692l.318.094c.835.246.835 1.428 0 1.674l-.319.094a1.873 1.873 0 0 0-1.115 2.693l.16.291c.415.764-.42 1.6-1.185 1.184l-.291-.159a1.873 1.873 0 0 0-2.693 1.116l-.094.318c-.246.835-1.428.835-1.674 0l-.094-.319a1.873 1.873 0 0 0-2.692-1.115l-.292.16c-.764.415-1.6-.42-1.184-1.185l.159-.291A1.873 1.873 0 0 0 1.945 8.93l-.319-.094c-.835-.246-.835-1.428 0-1.674l.319-.094A1.873 1.873 0 0 0 3.06 4.377l-.16-.292c-.415-.764.42-1.6 1.185-1.184l.292.159a1.873 1.873 0 0 0 2.692-1.115l.094-.319z"/>
            </svg>
          </button>
        </div>
        
        <!-- Timeline row (inside control bar, shows when toggled) -->
        <div v-if="showTimelinePanel && animationTimeKeys.length > 0" class="control-bar-timeline">
          <span class="timeline-label start">{{ timelineStartLabel }}</span>
          <div class="timeline-slider-container">
            <input type="range" 
                   class="timeline-slider"
                   :min="0" 
                   :max="animationTimeKeys.length - 1" 
                   :value="animationFrameIndex"
                   @input="seekToFrame(parseInt($event.target.value))"
                   @mousedown="onTimelineDragStart"
                   @mouseup="onTimelineDragEnd"
                   @touchstart="onTimelineDragStart"
                   @touchend="onTimelineDragEnd">
            <div class="timeline-current-time">{{ timelineCurrentLabel }}</div>
          </div>
          <span class="timeline-label end">{{ timelineEndLabel }}</span>
        </div>
      </div>
      
      <!-- Animation Settings Panel (expandable) -->
      <div v-if="showAnimationSettings" class="animation-settings-panel" @click.stop @dblclick.stop @mousedown.stop>
        <div class="settings-header">
          <strong>Animation Settings</strong>
          <button @click="showAnimationSettings = false" class="close-btn">×</button>
        </div>
        
        <!-- Speed control -->
        <div class="setting-row">
          <label>Speed: {{ playbackSpeed }}x</label>
          <input type="range" v-model.number="playbackSpeed" min="0.5" max="3" step="0.5" 
                 @input="adjustPlaybackSpeed" class="form-range">
        </div>
        
        <!-- Time step control -->
        <div class="setting-row">
          <div class="d-flex justify-content-between align-items-center">
            <label class="mb-0">Time Step: {{ currentTimeSkip }}m</label>
            <div class="form-check form-switch mb-0">
              <input type="checkbox" class="form-check-input" id="anim-auto-skip" v-model="autoTimeSkip">
              <label class="form-check-label small" for="anim-auto-skip">Auto</label>
            </div>
          </div>
          <div v-if="!autoTimeSkip" class="d-flex align-items-center gap-2 mt-1">
            <button @click="decreaseTimeSkip" class="btn btn-sm btn-outline-secondary py-0 px-2">−</button>
            <span class="small">{{ manualTimeSkip }}m</span>
            <button @click="increaseTimeSkip" class="btn btn-sm btn-outline-secondary py-0 px-2">+</button>
          </div>
        </div>
        
        <!-- Loop window control -->
        <div class="setting-row">
          <div class="d-flex justify-content-between align-items-center mb-1">
            <label class="mb-0">Loop Window</label>
            <button v-if="customStepsBack !== null || customStepsForward !== null" 
                    @click="resetLoopWindow" 
                    class="btn btn-link btn-sm p-0 text-muted" 
                    title="Reset to auto">
              Reset
            </button>
          </div>
          <div class="loop-window-display">{{ loopWindowLabel }}</div>
          
          <!-- Steps back slider -->
          <div class="slider-row">
            <span class="slider-label">Back:</span>
            <input type="range" 
                   :value="effectiveLoopSteps.stepsBack" 
                   @input="customStepsBack = parseInt($event.target.value)"
                   :min="0" 
                   :max="maxStepsBack" 
                   step="1" 
                   class="form-range flex-grow-1">
            <span class="slider-value">{{ effectiveLoopSteps.stepsBack }}</span>
          </div>
          
          <!-- Steps forward slider -->
          <div class="slider-row">
            <span class="slider-label">Forward:</span>
            <input type="range" 
                   :value="effectiveLoopSteps.stepsForward" 
                   @input="customStepsForward = parseInt($event.target.value)"
                   :min="0" 
                   :max="maxStepsForward" 
                   step="1" 
                   class="form-range flex-grow-1">
            <span class="slider-value">{{ effectiveLoopSteps.stepsForward }}</span>
          </div>
          
          <div class="small text-muted mt-1">
            Auto: {{ activeLoopSteps.stepsBack }} back, {{ activeLoopSteps.stepsForward }} forward
          </div>
        </div>
      </div>

      <!-- DateTime picker overlay -->
      <div v-if="showDateTimePicker" class="datetime-picker-modal">
        <div class="modal-content-custom">
          <h6>Select Date & Time (UTC)</h6>
          <input type="datetime-local" :value="dateTimeLocalValue" 
                 @change="onDateTimeInput" class="form-control">
          <div class="d-flex gap-2 mt-2">
            <button @click="showDateTimePicker = false" class="btn btn-secondary btn-sm flex-fill">Cancel</button>
            <button @click="applyDateTime" class="btn btn-primary btn-sm flex-fill">Apply</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Tile Error Banner -->
    <div v-if="tileError || tileErrorCount > 0" class="tile-error-banner alert alert-danger d-flex align-items-center">
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="me-2 flex-shrink-0" viewBox="0 0 16 16">
        <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"/>
      </svg>
      <div class="flex-grow-1">
        <strong>Map Tile Error</strong>
        <span v-if="tileErrorCount > 0" class="badge bg-danger ms-1">{{ tileErrorCount }}</span>
        <p class="mb-0 small">{{ tileError || 'Failed to load map tiles. Check your API key or connection.' }}</p>
      </div>
      <button @click="dismissTileError" class="btn btn-sm btn-outline-danger ms-2" title="Dismiss">
        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16">
          <path d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8 2.146 2.854Z"/>
        </svg>
      </button>
    </div>

    <!-- Animation Preload Overlay -->
    <div v-if="isPreloadingFrames" class="preload-overlay">
      <div class="preload-content">
        <svg class="spin" xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="currentColor" viewBox="0 0 16 16">
          <path d="M8 3a5 5 0 1 0 4.546 2.914.5.5 0 0 1 .908-.417A6 6 0 1 1 8 2z"/>
          <path d="M8 4.466V.534a.25.25 0 0 1 .41-.192l2.36 1.966c.12.1.12.284 0 .384L8.41 4.658A.25.25 0 0 1 8 4.466z"/>
        </svg>
        <div class="preload-text">Loading animation frames...</div>
        <div class="preload-progress">
          <div class="preload-progress-bar" :style="{ width: preloadProgress + '%' }"></div>
        </div>
        <div class="preload-percent">{{ preloadProgress }}%</div>
      </div>
    </div>

    <!-- Legend (positioned on map) - now shows all visible layers -->
    <div v-if="visibleLayerLegends.length > 0" class="legend-container" :class="{ 'legend-collapsed': legendCollapsed }">
      <div class="legend-header" @click="legendCollapsed = !legendCollapsed">
        <strong>Legend</strong>
        <svg v-if="legendCollapsed" xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
          <path fill-rule="evenodd" d="M7.646 4.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1-.708.708L8 5.707l-5.646 5.647a.5.5 0 0 1-.708-.708l6-6z"/>
        </svg>
        <svg v-else xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
          <path fill-rule="evenodd" d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z"/>
        </svg>
      </div>
      <div v-if="!legendCollapsed" class="legend-content">
        <div v-for="legend in visibleLayerLegends" :key="legend.layerId" class="legend-item">
          <div class="legend-item-header" @click="toggleLegendExpand(legend.layerId)">
            <span class="legend-item-title">{{ legend.title }}</span>
            <svg v-if="expandedLegends[legend.layerId]" xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16">
              <path fill-rule="evenodd" d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z"/>
            </svg>
            <svg v-else xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16">
              <path fill-rule="evenodd" d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708z"/>
            </svg>
          </div>
          <div v-if="expandedLegends[legend.layerId]" class="legend-item-content" v-html="legend.html"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Patch Leaflet Tooltip to prevent errors during zoom animations when map is null
const originalTooltipAnimateZoom = L.Tooltip.prototype._animateZoom;
L.Tooltip.prototype._animateZoom = function(e) {
  if (!this._map) return;
  originalTooltipAnimateZoom.call(this, e);
};

const originalTooltipUpdatePosition = L.Tooltip.prototype._updatePosition;
L.Tooltip.prototype._updatePosition = function() {
  if (!this._map) return;
  originalTooltipUpdatePosition.call(this);
};

const originalTooltipUpdate = L.Tooltip.prototype.update;
L.Tooltip.prototype.update = function() {
  if (!this._map) return this;
  return originalTooltipUpdate.call(this);
};

// Patch Leaflet Popup as well for consistency
const originalPopupAnimateZoom = L.Popup.prototype._animateZoom;
L.Popup.prototype._animateZoom = function(e) {
  if (!this._map) return;
  originalPopupAnimateZoom.call(this, e);
};

const originalPopupUpdatePosition = L.Popup.prototype._updatePosition;
L.Popup.prototype._updatePosition = function() {
  if (!this._map) return;
  originalPopupUpdatePosition.call(this);
};

const originalPopupUpdate = L.Popup.prototype.update;
L.Popup.prototype.update = function() {
  if (!this._map) return this;
  return originalPopupUpdate.call(this);
};

// Patch Leaflet GridLayer animation methods to prevent null reference errors
// These errors occur when layers receive zoom events after being removed from the map
const originalGridLayerAnimateZoom = L.GridLayer.prototype._animateZoom;
L.GridLayer.prototype._animateZoom = function(e) {
  if (!this._map) return;
  try {
    originalGridLayerAnimateZoom.call(this, e);
  } catch (err) {
    // Silently ignore errors during animation if map was destroyed mid-animation
    if (!this._map) return;
    throw err;
  }
};

const originalGridLayerSetView = L.GridLayer.prototype._setView;
L.GridLayer.prototype._setView = function(center, zoom, noPrune, noUpdate) {
  if (!this._map) return;
  try {
    originalGridLayerSetView.call(this, center, zoom, noPrune, noUpdate);
  } catch (err) {
    if (!this._map) return;
    throw err;
  }
};

const originalGridLayerUpdateLevels = L.GridLayer.prototype._updateLevels;
L.GridLayer.prototype._updateLevels = function() {
  if (!this._map) return;
  try {
    return originalGridLayerUpdateLevels.call(this);
  } catch (err) {
    if (!this._map) return;
    throw err;
  }
};

const originalGridLayerOnZoomEnd = L.GridLayer.prototype._onZoomEnd;
L.GridLayer.prototype._onZoomEnd = function() {
  if (!this._map) return;
  try {
    originalGridLayerOnZoomEnd.call(this);
  } catch (err) {
    if (!this._map) return;
    throw err;
  }
};

// Also patch _update and _resetView which can be called during/after animations
const originalGridLayerUpdate = L.GridLayer.prototype._update;
L.GridLayer.prototype._update = function(center) {
  if (!this._map) return;
  try {
    return originalGridLayerUpdate.call(this, center);
  } catch (err) {
    if (!this._map) return;
    throw err;
  }
};

const originalGridLayerResetView = L.GridLayer.prototype._resetView;
L.GridLayer.prototype._resetView = function(e) {
  if (!this._map) return;
  try {
    originalGridLayerResetView.call(this, e);
  } catch (err) {
    if (!this._map) return;
    throw err;
  }
};

export default {
  name: "WeatherMapAi",
  data() {
    return {
      // ---- Session/Auth State ----
      // These are populated from window.vcCommunicator on mount
      sessionId: null,
      sessionState: null,
      apiKeyOverride: '', // User-provided API key override
      
      // ---- Tile Error State ----
      tileError: null, // Error message to display when tiles fail
      tileErrorTimeout: null, // Timeout to auto-dismiss error
      tileErrorCount: 0, // Total count of tile errors since last clear
      layerErrors: {}, // Track error count per layer id
      
      map: null,
      mapReady: false,
      controlsOpen: false,
      legendCollapsed: false,
      showAdvanced: false,
      advancedEnabled: false, // Only show Advanced section when URL has showadvanced=true
      showIntegration: false,
      strictMode: true, // Send strict=true/false to map tile API
      expandedLegends: {}, // Track which legend items are expanded
      
      // Model definitions with elements and time settings
      // Each model has default timeSkip/loopSteps, elements can override
      modelOptions: {
        default: { 
          elements: {
            // Radar-based elements - use default timeSkip (10), override loopSteps for past-only
            precipcomposite: { loopSteps: { stepsBack: 12, stepsForward: 0 } },
            preciprate: { loopSteps: { stepsBack: 12, stepsForward: 0 } },
            preciptype: { loopSteps: { stepsBack: 12, stepsForward: 0 } },
            precip: {},
            hailsize: { loopSteps: { stepsBack: 12, stepsForward: 0 } },
            // Forecast elements - longer timeSkip and future window
            temp: { timeSkip: 60, loopSteps: { stepsBack: 0, stepsForward: 120 } },
            cloudcover: { timeSkip: 60, loopSteps: { stepsBack: 0, stepsForward: 120 } },
            sealevelpressure: { timeSkip: 60, loopSteps: { stepsBack: 0, stepsForward: 120 } },
            windspeed: { timeSkip: 60, loopSteps: { stepsBack: 0, stepsForward: 120 } },
            winddir: { timeSkip: 60, loopSteps: { stepsBack: 0, stepsForward: 120 } },
            wind: { timeSkip: 60, loopSteps: { stepsBack: 0, stepsForward: 120 } },
            alerts: {},
            // Satellite elements - use default timeSkip (10), override loopSteps for past-only
            cloudtopheight: { loopSteps: { stepsBack: 12, stepsForward: 0 } },
            cloudtoptemp: { loopSteps: { stepsBack: 12, stepsForward: 0 } },
            cloudopticaldepth: { loopSteps: { stepsBack: 12, stepsForward: 0 } },
            cloudphase: { loopSteps: { stepsBack: 12, stepsForward: 0 } },
            solarradiation: { loopSteps: { stepsBack: 12, stepsForward: 0 } },
            abi_rad_c02: { loopSteps: { stepsBack: 12, stepsForward: 0 } },
            abi_rad_c07: { loopSteps: { stepsBack: 12, stepsForward: 0 } }
          },
          timeSkip: 10,
          timeSkipOverride: false,
          loopSteps: { stepsBack: 12, stepsForward: 12 }
        },
        gfs: { 
          elements: {
            precipcomposite: {}, preciprate: {}, preciptype: {}, precip: {}, 
            temp: {}, cloudcover: {}, windspeed: {}, winddir: {}, wind: {}
          },
          timeSkip: 60,
          loopSteps: { stepsBack: 0, stepsForward: 120 }
        },
        iconeu: { 
          elements: {
            precipcomposite: {}, preciprate: {}, preciptype: {}, precip: {}, 
            temp: {}, cloudcover: {}, windspeed: {}, winddir: {}, wind: {}
          },
          timeSkip: 60,
          loopSteps: { stepsBack: 0, stepsForward: 120 }
        },
        hrrr: { 
          elements: {
            precipcomposite: {}, preciprate: {}, preciptype: {}, precip: {}, 
            temp: {}, cloudcover: {}, windspeed: {}, winddir: {}, wind: {}
          },
          timeSkip: 60,
          loopSteps: { stepsBack: 0, stepsForward: 120 }
        },
        goes_10min_location_2: { 
          elements: {
            cloudtopheight: {}, cloudtoptemp: {}, cloudopticaldepth: {}, cloudphase: {},
            solarradiation: {}, abi_rad_c02: {}, abi_rad_c07: {}, preciprate: {},
            cloudmask: {}, total_precipitable_water: {}, cloudcover: {},
            lowcloudcover: {}, medcloudcover: {}, hicloudcover: {}, precip_index: {}
          },
          timeSkip: 10,
          timeSkipOverride: false,
          loopSteps: { stepsBack: 12, stepsForward: 0 }
        },
        mrms_2min_location_2: { 
          elements: {
            precipcomposite: {}, preciprate: {}, preciptype: {}, hailsize: {}, reflectivity: {}
          },
          timeSkip: 2,
          timeSkipOverride: false,
          loopSteps: { stepsBack: 60, stepsForward: 0 }
        },
        ukmet_15min_location_2: { 
          elements: { preciprate: {} },
          timeSkip: 15,
          timeSkipOverride: false,
          loopSteps: { stepsBack: 8, stepsForward: 0 }
        },
        dwd_5min_location_2: { 
          elements: {
            precipcomposite: {}, preciprate: {}, preciptype: {}, reflectivity: {}
          },
          timeSkip: 2,
          timeSkipOverride: false,
          loopSteps: { stepsBack: 60, stepsForward: 0 }
        },
        hrrr_location_2: { 
          elements: {
            precipcomposite: {}, preciprate: {}, preciptype: {}, precip: {},
            temp: {}, feelslike: {}, dew: {}, humidity: {}, windspeed: {}, winddir: {}, wind: {}
          },
          timeSkip: 60,
          loopSteps: { stepsBack: 0, stepsForward: 120 }
        },
        iconeu_location_2: { 
          elements: {
            precipcomposite: {}, preciprate: {}, preciptype: {}, precip: {},
            temp: {}, feelslike: {}, dew: {}, humidity: {}, windspeed: {}, winddir: {}, wind: {}
          },
          timeSkip: 60,
          loopSteps: { stepsBack: 0, stepsForward: 120 }
        },
        gfs_location_2: { 
          elements: {
            precipcomposite: {}, preciprate: {}, preciptype: {}, precip: {},
            temp: {}, feelslike: {}, dew: {}, humidity: {}, cloudcover: {},
            windspeed: {}, winddir: {}, wind: {}
          },
          timeSkip: 60,
          loopSteps: { stepsBack: 0, stepsForward: 120 }
        },
        ecmwf_location_2: { 
          elements: {
            temp: {}, feelslike: {}, dew: {}, humidity: {}, preciprate: {},
            precip: {}, cloudcover: {}, windspeed: {}, winddir: {}, wind: {}
          },
          timeSkip: 60,
          loopSteps: { stepsBack: 0, stepsForward: 120 }
        },
        ecmwfaifs_location_2: { 
          elements: {
            temp: {}, feelslike: {}, dew: {}, humidity: {}, preciprate: {},
            cloudcover: {}, windspeed: {}, winddir: {}, wind: {}
          },
          timeSkip: 60,
          loopSteps: { stepsBack: 0, stepsForward: 120 }
        },
        weatherRadarSubhourly_location: { 
          elements: { reflectivity: {} },
          timeSkip: 10,
          loopSteps: { stepsBack: 12, stepsForward: 12 }
        }
      },
      
      // Element display names
      elementNames: {
        precipcomposite: "Precipitation Composite",
        preciprate: "Precipitation Rate",
        preciptype: "Precipitation Type",
        precip: "Precipitation",
        hailsize: "Hail Size",
        temp: "Temperature",
        feelslike: "Feels Like",
        dew: "Dew Point",
        humidity: "Humidity",
        cloudcover: "Cloud Cover",
        sealevelpressure: "Sea Level Pressure",
        windspeed: "Wind Speed",
        winddir: "Wind Direction",
        wind: "Wind",
        alerts: "Weather Alerts",
        cloudtopheight: "Cloud Top Height",
        cloudtoptemp: "Cloud Top Temperature",
        cloudopticaldepth: "Cloud Optical Depth",
        cloudphase: "Cloud Phase",
        solarradiation: "Solar Radiation",
        abi_rad_c02: "ABI Radiance C02",
        abi_rad_c07: "ABI Radiance C07",
        reflectivity: "Radar Reflectivity"
      },
      
      // Model display names
      modelNames: {
        default: "Default",
        goes_10min_location_2: "GOES Satellite",
        mrms_2min_location_2: "MRMS Radar",
        ukmet_15min_location_2: "UK Met Office",
        dwd_5min_location_2: "DWD Radar",
        hrrr_location_2: "HRRR",
        iconeu_location_2: "ICON-EU",
        gfs_location_2: "GFS",
        ecmwf_location_2: "ECMWF",
        ecmwfaifs_location_2: "ECMWF AIFS",
        weatherRadarSubhourly_location: "Weather Radar"
      },
      
      // Active layers on the map
      activeLayers: [],
      nextLayerId: 1,
      
      // Add layer modal
      showAddLayerModal: false,
      newLayerModel: 'Default',
      newLayerElement: 'precipcomposite',
      
      // Base maps
      selectedBaseMap: 'carto-light',
      baseMaps: {
        'carto-light': {
          url: 'https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png',
          attribution: '&copy; <a href="https://carto.com">CARTO</a>'
        },
        'carto-dark': {
          url: 'https://{s}.basemaps.cartocdn.com/dark_nolabels/{z}/{x}/{y}{r}.png',
          attribution: '&copy; <a href="https://carto.com">CARTO</a>'
        },
        'osm': {
          url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
          attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        },
        'satellite': {
          url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
          attribution: 'Tiles &copy; Esri'
        },
        'vcosm1': {
          url: 'https://osm1.visualcrossing.com/osm/{z}/{x}/{y}.png',
          attribution: 'Tiles &copy; VC'
        },
        'vcosm2': {
          url: 'https://osm2.visualcrossing.com/osm/{z}/{x}/{y}.png',
          attribution: 'Tiles &copy; VC'
        }
      },
      baseLayer: null,
      labelLayer: null,
      showLabels: false,
      
      // Tile grid debug overlay
      showTileGrid: false,
      tileGridLayer: null,
      
      // GeoJSON cities layer
      showGeoJson: false,
      geoJsonElement: 'temp',
      geoJsonLayer: null,
      geoJsonTiles: {},
      geoJsonAbortControllers: new Map(),
      
      // Time management
      currentTime: null,
      isPlaying: false,
      playbackSpeed: 1.0,
      movieInterval: null,
      showDateTimePicker: false,
      tempDateTimeValue: '',
      autoTimeSkip: true,
      manualTimeSkip: 10,
      timeSkipPresets: [1, 2, 5, 10, 15, 30, 60],
      
      // Animation UI
      showAnimationSettings: false, // Expandable panel for speed/loop settings
      showTimelinePanel: false, // Expandable panel for timeline slider
      // Custom loop window as step counts (user-adjustable)
      // null means use the auto-calculated activeLoopSteps
      customStepsBack: null,    // Number of steps back from current time
      customStepsForward: null, // Number of steps forward from current time
      // Max bounds for step sliders
      maxStepsBack: 72,     // Max steps back (e.g., 72 * 10min = 12 hours)
      maxStepsForward: 72,  // Max steps forward
      // Timeline scrubber state
      isTimelineDragging: false, // True while user is dragging the timeline slider
      wasPlayingBeforeDrag: false, // Remember if animation was playing before drag
      
      // Animation double-buffering
      // True double-buffer: only 2 layers per element (front + back)
      // { layerId: { front: leafletLayer, back: leafletLayer, frontTimeKey: string, backTimeKey: string } }
      animationBuffers: {},
      // Current frame index for animation
      animationFrameIndex: 0,
      // List of time keys for current animation loop
      animationTimeKeys: [],
      // Whether animation frames are being preloaded
      isPreloadingFrames: false,
      // Maximum number of frames to preload (memory management)
      // Needs to accommodate large loop windows with small time steps
      maxAnimationFrames: 120,
      // Preload progress (0-100)
      preloadProgress: 0,
      // Debounce timer for animation restart (avoids rapid restarts during slider drag)
      animationRestartTimer: null,
      // Flag to track if next frame is ready in double-buffer mode
      nextFrameReady: false,
      
      // ---- Animation Mode Switch ----
      // true = use canvas rasterization (lower memory, smoother)
      // false = use L.tileLayer opacity swapping (current approach)
      // NOTE: Canvas mode requires CORS headers from tile server - disabled for now
      useCanvasAnimation: true,
      
      // Canvas animation state
      // Stores rasterized frame images as data URLs or ImageBitmap objects
      canvasFrameCache: [], // Array of { timeKey, imageData, img } objects (img = preloaded Image)
      // Reference to the animation canvas element
      animationCanvas: null,
      // Animation frame request ID for requestAnimationFrame
      animationFrameRequestId: null,
      // Crossfade animation state
      crossfadeProgress: 0, // 0-1 progress between current and next frame
      crossfadeLastTime: 0, // Last timestamp for animation timing
      crossfadeEnabled: true, // Enable/disable crossfade blending
      
      // Settings
      unitGroup: 'metric',
      baseApiUrl: 'https://maps.visualcrossing.com',
      defaultApiKey: '', // Fallback API key when no session or override
      
      // UI state
      viewInfo: {
        lat: null,
        lng: null,
        zoom: null,
        centerPair: ''
      },
      
      // Legend - now stores legends for all visible layers
      visibleLayerLegends: [],
      elementBinCache: {},

      // User markers
      userMarkers: [],
      nextMarkerId: 1,
      markerColors: ['#e74c3c', '#3498db', '#2ecc71', '#f39c12', '#9b59b6', '#1abc9c'],
      newMarkerLatLng: '',
      userLocationMarker: null,
      userLocation: null,
      // Throttle timer for updateViewInfo to prevent excessive calls during rapid pan
      updateViewInfoTimer: null
    };
  },
  
  computed: {
    /**
     * Check if user has an active session
     */
    hasSession() {
      return !!this.sessionId && !!this.sessionState;
    },
    
    /**
     * Get the effective API key to use for map tile requests
     * Priority: 1) User override, 2) Session key, 3) Default key
     */
    weatherApiKey() {
      if (this.apiKeyOverride && this.apiKeyOverride.trim()) {
        return this.apiKeyOverride.trim();
      }
      if (this.sessionState?.apiKey) {
        return this.sessionState.apiKey;
      }
      return this.defaultApiKey;
    },
    
    /**
     * Check if using a custom API key (not the default)
     * Used to determine whether to show key in UI and sample URLs
     */
    hasCustomApiKey() {
      // True if we have an override or a session key (i.e., not just using the default)
      return !!this.apiKeyOverride?.trim() || !!this.sessionState?.apiKey;
    },
    
    /**
     * Check if we have a valid API key available (any non-empty key)
     */
    hasValidApiKey() {
      return !!this.weatherApiKey;
    },
    
    /**
     * Get the API key to display in the text box
     * Shows: manual override or session key (but NOT the default key)
     * Returns empty string if only using default key
     */
    displayApiKey: {
      get() {
        // If user has typed something, show that
        if (this.apiKeyOverride && this.apiKeyOverride.trim()) {
          return this.apiKeyOverride;
        }
        // If session has a key, show it (so user can see/edit it)
        if (this.sessionState?.apiKey) {
          return this.sessionState.apiKey;
        }
        // Otherwise show empty (using default key behind the scenes)
        return '';
      },
      set(value) {
        // When user types, update the override
        this.apiKeyOverride = value;
      }
    },
    
    formattedTime() {
      return this.currentTime ? this.formatFullDateTime(this.currentTime) : '';
    },
    dateTimeLocalValue() {
      if (!this.currentTime) return '';
      const pad = n => String(n).padStart(2, '0');
      const y = this.currentTime.getUTCFullYear();
      const m = pad(this.currentTime.getUTCMonth() + 1);
      const d = pad(this.currentTime.getUTCDate());
      const h = pad(this.currentTime.getUTCHours());
      const min = pad(this.currentTime.getUTCMinutes());
      return `${y}-${m}-${d}T${h}:${min}`;
    },
    availableElements() {
      const model = this.modelOptions[this.newLayerModel];
      return model ? Object.keys(model.elements) : [];
    },
    // Get the smallest timeSkip from visible layers for playback
    activeTimeSkip() {
      const visibleLayers = this.activeLayers.filter(l => l.visible);
      if (visibleLayers.length === 0) return 10;
      return Math.min(...visibleLayers.map(l => this.getElementConfig(l.model, l.element).timeSkip));
    },
    currentTimeSkip() {
      return this.autoTimeSkip ? this.activeTimeSkip : this.manualTimeSkip;
    },
    /**
     * Calculate the effective loop steps based on all visible layers.
     * Uses the intersection (most restrictive) of all layer loop steps.
     * This ensures playback stays within bounds valid for all layers.
     */
    activeLoopSteps() {
      const visibleLayers = this.activeLayers.filter(l => l.visible);
      if (visibleLayers.length === 0) {
        return { stepsBack: 12, stepsForward: 12 };
      }
      
      // Get all loop steps from visible layers
      const loopSteps = visibleLayers.map(l => this.getElementConfig(l.model, l.element).loopSteps);
      
      // Find the intersection: min of all stepsBack and stepsForward
      const stepsBack = Math.min(...loopSteps.map(s => s.stepsBack));
      const stepsForward = Math.min(...loopSteps.map(s => s.stepsForward));
      
      return { stepsBack, stepsForward };
    },
    
    /**
     * The effective loop steps used for animation.
     * Uses custom values if set, otherwise falls back to activeLoopSteps.
     */
    effectiveLoopSteps() {
      return {
        stepsBack: this.customStepsBack ?? this.activeLoopSteps.stepsBack,
        stepsForward: this.customStepsForward ?? this.activeLoopSteps.stepsForward
      };
    },
    
    /**
     * Format loop steps as human-readable label showing total frames.
     */
    loopWindowLabel() {
      const { stepsBack, stepsForward } = this.effectiveLoopSteps;
      const totalFrames = stepsBack + stepsForward + 1; // +1 for current time frame
      return `${stepsBack} back, ${stepsForward} forward (${totalFrames} frames)`;
    },
    
    /**
     * Format a short time label (HH:mm) from an ISO time key string.
     */
    timelineStartLabel() {
      if (this.animationTimeKeys.length === 0) return '';
      return this.formatShortTime(this.animationTimeKeys[0]);
    },
    
    timelineEndLabel() {
      if (this.animationTimeKeys.length === 0) return '';
      return this.formatShortTime(this.animationTimeKeys[this.animationTimeKeys.length - 1]);
    },
    
    timelineCurrentLabel() {
      if (this.animationTimeKeys.length === 0 || this.animationFrameIndex < 0) return '';
      const timeKey = this.animationTimeKeys[this.animationFrameIndex];
      if (!timeKey) return '';
      return this.formatShortTime(timeKey);
    }
  },
  
  watch: {
    unitGroup() {
      if (!this.mapReady || !this.map) return;
      this.refreshAllLayers();
      if (this.showGeoJson) {
        this.refreshGeoJsonTiles();
      }
      this.saveSettings();
    },
    baseApiUrl() {
      if (!this.mapReady || !this.map) return;
      this.refreshAllLayers();
      if (this.showGeoJson) {
        this.refreshGeoJsonTiles();
      }
      this.saveSettings();
    },
    apiKeyOverride() {
      // When API key changes, clear errors and refresh layers
      this.dismissTileError();
      if (!this.mapReady || !this.map) return;
      this.refreshAllLayers();
      if (this.showGeoJson) {
        this.refreshGeoJsonTiles();
      }
    },
    selectedBaseMap() {
      this.saveSettings();
    },
    showLabels() {
      this.saveSettings();
    },
    showGeoJson() {
      this.saveSettings();
    },
    geoJsonElement() {
      this.saveSettings();
    },
    playbackSpeed() {
      this.saveSettings();
    },
    autoTimeSkip() {
      this.saveSettings();
      // Time skip affects frame generation - restart animation if playing
      this.restartAnimationIfPlaying();
    },
    manualTimeSkip() {
      this.saveSettings();
      // Time skip affects frame generation - restart animation if playing
      if (!this.autoTimeSkip) {
        this.restartAnimationIfPlaying();
      }
    },
    // Watch for custom loop steps changes
    customStepsBack() {
      this.restartAnimationIfPlaying();
    },
    customStepsForward() {
      this.restartAnimationIfPlaying();
    },
    activeLayers: {
      deep: true,
      handler(newVal, oldVal) {
        // Skip during animation to prevent infinite restart loops
        // The deep watcher can fire when we access layers during frame display
        if (this.isPlaying || this.isPreloadingFrames) {
          return;
        }
        
        if (this.mapReady) {
          this.saveSettings();
          // Layer changes affect animation - but only restart if animation was playing before this change
          // Don't call restartAnimationIfPlaying here - it's checked above
        }
      }
    }
  },
  
  mounted() {
    /**
     * Session/Auth Initialization
     * 
     * Uses window.vcCommunicator to:
     * 1. Get initial session state if already logged in
     * 2. Listen for 'createsession' events when user logs in
     * 3. Listen for 'terminatesession' events when user logs out
     * 
     * The sessionState object contains:
     * - apiKey: Used for map tile API requests
     * - userId: User identifier
     */
    const vcCommunicator = window.vcCommunicator;
    
    if (vcCommunicator) {
      // Get initial session state
      this.sessionId = vcCommunicator.sessionId;
      this.sessionState = vcCommunicator.sessionState;
      
      // Listen for session creation
      vcCommunicator.addHandler('createsession', (sessionId, sessionState) => {
        this.sessionId = sessionId;
        this.sessionState = sessionState;
        // Clear any override when logging in with a new session
        if (!this.apiKeyOverride) {
          this.refreshAllLayers();
        }
      });
      
      // Listen for session termination
      vcCommunicator.addHandler('terminatesession', () => {
        this.sessionId = null;
        this.sessionState = null;
        // Keep any user-provided override
        if (!this.apiKeyOverride) {
          this.refreshAllLayers();
        }
      });
    }
    
    // Check URL parameter for advanced mode
    const urlParams = new URLSearchParams(window.location.search);
    this.advancedEnabled = urlParams.get('showadvanced') === 'true';
    
    this.loadSettings(); // Load saved settings first
    this.initializeMap();
    this.currentTime = this.getLastInterval();
    this.mapReady = true;
    
    // Add a default layer only if none were loaded from storage
    if (this.activeLayers.length === 0) {
      this.addDefaultLayers();
    } else {
      // Create Leaflet layers for loaded layers
      this.activeLayers.forEach(layer => {
        if (layer.visible) {
          this.createLeafletLayer(layer);
          this.expandedLegends[layer.id] = true;
        }
      });
      this.updateAllLegends();
    }
    
    // Apply saved overlay settings
    if (this.showLabels) {
      this.addLabelsLayer();
    }
    if (this.showGeoJson) {
      this.addGeoJsonTileLayer();
    }
    
    this.$nextTick(() => {
      this.updateViewInfo();
    });
  },
  
  beforeUnmount() {
    this.stopMovie();
    this.mapReady = false;
    
    // Clear canvas frame cache explicitly
    this.clearCanvasCache();
    
    // Clear any pending tile error timeout
    if (this.tileErrorTimeout) {
      clearTimeout(this.tileErrorTimeout);
      this.tileErrorTimeout = null;
    }
    
    // Clear any pending animation restart timer
    if (this.animationRestartTimer) {
      clearTimeout(this.animationRestartTimer);
      this.animationRestartTimer = null;
    }
    
    // Clear any pending updateViewInfo timer
    if (this.updateViewInfoTimer) {
      clearTimeout(this.updateViewInfoTimer);
      this.updateViewInfoTimer = null;
    }
    
    // Remove all event listeners from map first
    if (this.map) {
      this.map.off('moveend', this.updateViewInfo);
      this.map.off('zoomend', this.updateViewInfo);
      this.map.off('movestart', this.handleMapMoveForAnimation);
      this.map.off('zoomstart', this.handleMapMoveForAnimation);
      this.map.off('click'); // Remove inline click handler
      // Stop any ongoing zoom animation
      this.map.stop();
    }
    
    // Clean up GeoJSON layer FIRST (before other layers) to prevent animation errors
    // Remove event listeners and detach from map before cleanup
    if (this.geoJsonLayer) {
      this.geoJsonLayer.off();
      if (this.geoJsonLayer._map) {
        this.map.removeLayer(this.geoJsonLayer);
      }
      this.geoJsonLayer = null;
    }
    
    // Abort all pending GeoJSON requests
    this.geoJsonAbortControllers.forEach(ctrl => ctrl.abort());
    this.geoJsonAbortControllers.clear();
    
    // Clean up GeoJSON tile groups
    Object.keys(this.geoJsonTiles).forEach(key => {
      this.safeRemoveLayer(this.geoJsonTiles[key]);
    });
    this.geoJsonTiles = {};
    
    // Clean up all active weather layers
    this.activeLayers.forEach(layer => {
      this.safeRemoveLayer(layer.leafletLayer);
      layer.leafletLayer = null;
    });
    
    // Clean up user markers and their tooltips
    this.userMarkers.forEach(marker => {
      if (marker.leafletMarker) {
        marker.leafletMarker.unbindTooltip();
        this.safeRemoveLayer(marker.leafletMarker);
        marker.leafletMarker = null;
      }
    });
    
    // Clean up labels layer
    this.safeRemoveLayer(this.labelLayer);
    this.labelLayer = null;
    
    // Clean up base layer
    this.safeRemoveLayer(this.baseLayer);
    this.baseLayer = null;
    
    // Finally remove the map
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  },
  
  methods: {
    /**
     * Get effective config for a model+element combination
     * Element-level settings override model-level defaults
     */
    getElementConfig(modelId, elementId) {
      const model = this.modelOptions[modelId] || this.modelOptions.default;
      const elementConfig = model.elements?.[elementId] || {};
      
      return {
        timeSkip: elementConfig.timeSkip ?? model.timeSkip ?? 10,
        timeSkipOverride: elementConfig.timeSkipOverride ?? model.timeSkipOverride ?? false,
        loopSteps: elementConfig.loopSteps ?? model.loopSteps ?? { stepsBack: 12, stepsForward: 12 }
      };
    },
    
     initializeMap() {
      this.map = L.map("map", {
        zoomControl: false
      }).setView([0, 0], 7);
      
      L.control.zoom({ position: 'topleft' }).addTo(this.map);

      // Add custom layers toggle control
      const self = this;
      const LayersToggle = L.Control.extend({
        options: {
          position: 'topleft'
        },
        onAdd: function() {
          const container = L.DomUtil.create('div', 'leaflet-bar leaflet-control');
          const button = L.DomUtil.create('a', 'layers-control-btn', container);
          button.href = '#';
          button.title = 'Toggle Layers';
          button.setAttribute('role', 'button');
          button.setAttribute('aria-label', 'Toggle Layers');
          button.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
            <path d="M8.235 1.559a.5.5 0 0 0-.47 0l-7.5 4a.5.5 0 0 0 0 .882L3.188 8 .264 9.559a.5.5 0 0 0 0 .882l7.5 4a.5.5 0 0 0 .47 0l7.5-4a.5.5 0 0 0 0-.882L12.813 8l2.922-1.559a.5.5 0 0 0 0-.882l-7.5-4zM8 9.433 1.562 6 8 2.567 14.438 6 8 9.433z"/>
          </svg>`;
          
          L.DomEvent.on(button, 'click', function(e) {
            L.DomEvent.preventDefault(e);
            L.DomEvent.stopPropagation(e);
            self.controlsOpen = !self.controlsOpen;
          });
          
          return container;
        }
      });
      
      new LayersToggle().addTo(this.map);
      
      // Add center on current location control
      const CenterLocationControl = L.Control.extend({
        options: {
          position: 'topleft'
        },
        onAdd: function() {
          const container = L.DomUtil.create('div', 'leaflet-bar leaflet-control');
          const button = L.DomUtil.create('a', 'center-location-btn', container);
          button.href = '#';
          button.title = 'Center on current location';
          button.setAttribute('role', 'button');
          button.setAttribute('aria-label', 'Center on current location');
          button.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
            <path d="M8 16s6-5.686 6-10A6 6 0 0 0 2 6c0 4.314 6 10 6 10zm0-7a3 3 0 1 1 0-6 3 3 0 0 1 0 6z"/>
          </svg>`;
          
          L.DomEvent.on(button, 'click', function(e) {
            L.DomEvent.preventDefault(e);
            L.DomEvent.stopPropagation(e);
            self.goToUserLocation();
          });
          
          return container;
        }
      });
      
      new CenterLocationControl().addTo(this.map);
      
      // Create panes FIRST
      this.map.createPane('weatherPane');
      this.map.getPane('weatherPane').style.zIndex = 400;
      // Add smooth opacity transition for tile updates during playback
      this.map.getPane('weatherPane').style.transition = 'opacity 0.15s ease-in-out';
      
      this.map.createPane('labelsPane');
      this.map.getPane('labelsPane').style.zIndex = 500;
      
      this.map.createPane('citiesPane');
      this.map.getPane('citiesPane').style.zIndex = 600;
      
      this.map.createPane('markersPane');
      this.map.getPane('markersPane').style.zIndex = 650;

      // Then initialize base map
      this.changeBaseMap();
      
      this.map.on('moveend', this.updateViewInfo);
      this.map.on('zoomend', this.updateViewInfo);
      
      // Stop animation when map is panned or zoomed (tiles need to reload)
      this.map.on('movestart', this.handleMapMoveForAnimation);
      this.map.on('zoomstart', this.handleMapMoveForAnimation);
      
      // Ctrl+click to add a marker
      this.map.on('click', (e) => {
        if (e.originalEvent.ctrlKey) {
          this.addCustomMarker(e.latlng.lat, e.latlng.lng);
        }
      });
      
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const { latitude, longitude } = position.coords;
            this.userLocation = { lat: latitude, lng: longitude };
            this.map.setView([latitude, longitude], 8);
             this.addCustomMarker(latitude, longitude, "#4285f4");
          },
          (error) => console.error("Geolocation error:", error)
        );
      }
    },
    
    
    addDefaultLayers() {
      // Add MRMS Radar as default
      this.activeLayers.push({
        id: this.nextLayerId++,
        model: 'default',
        element: 'precipcomposite',
        visible: true,
        opacity: 0.7,
        leafletLayer: null
      });
      
      this.activeLayers.forEach(layer => {
        if (layer.visible) {
          this.createLeafletLayer(layer);
          // Expand first layer's legend by default
          this.expandedLegends[layer.id] = true;
        }
      });
      
      this.updateAllLegends();
    },
    /*
     addUserLocationMarker(lat, lng) {
      if (this.userLocationMarker) {
        this.map.removeLayer(this.userLocationMarker);
      }
      
      const icon = L.divIcon({
        className: 'user-location-marker',
        html: `<div style="width:16px;height:16px;background:#4285f4;border:3px solid white;border-radius:50%;box-shadow:0 2px 6px rgba(0,0,0,0.3);"></div>`,
        iconSize: [16, 16],
        iconAnchor: [8, 8]
      });
      
      this.userLocationMarker = L.marker([lat, lng], { 
        icon, 
        pane: 'markersPane',
        zIndexOffset: 1000 
      }).addTo(this.map);
      
      this.userLocationMarker.bindTooltip('Your Location', { direction: 'top', offset: [0, -10] });
    },
    */
    addMarkerFromInput() {
      const input = this.newMarkerLatLng.trim();
      if (!input) return;
      
      // Parse "lat,lng" or "lat lng"
      const parts = input.split(/[,\s]+/).map(s => parseFloat(s.trim()));
      if (parts.length >= 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
        this.addCustomMarker(parts[0], parts[1]);
        this.newMarkerLatLng = '';
      }
    },
    
    addMarkerFromGPS() {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const { latitude, longitude } = position.coords;
            this.addCustomMarker(latitude, longitude);
          },
          (error) => console.error("Geolocation error:", error)
        );
      }
    },
    
    addCustomMarker(lat, lng, color) {
      if (!this.map || !this.mapReady) return;
      
      color = color || this.markerColors[this.userMarkers.length % this.markerColors.length];
      const id = this.nextMarkerId++;
      
      const icon = L.divIcon({
        className: 'custom-marker',
        html: `<div style="width:14px;height:14px;background:${color};border:2px solid white;border-radius:50%;box-shadow:0 2px 4px rgba(0,0,0,0.3);"></div>`,
        iconSize: [14, 14],
        iconAnchor: [7, 7]
      });
      
      const marker = L.marker([lat, lng], { 
        icon, 
        pane: 'markersPane',
        draggable: true 
      }).addTo(this.map);
      
      marker.bindTooltip(`${lat.toFixed(4)}, ${lng.toFixed(4)}`, {
        direction: 'top',
        offset: [0, -8]
      });
      
      marker.on('dragend', () => {
        const pos = marker.getLatLng();
        const m = this.userMarkers.find(m => m.id === id);
        if (m) {
          m.lat = pos.lat;
          m.lng = pos.lng;
          marker.setTooltipContent(`${pos.lat.toFixed(4)}, ${pos.lng.toFixed(4)}`);
        }
      });
      
      this.userMarkers.push({ id, lat, lng, color, leafletMarker: marker });
    },
    
    removeMarker(index) {
      const marker = this.userMarkers[index];
      if (marker.leafletMarker) {
        // Unbind tooltip before removing to prevent animation errors
        marker.leafletMarker.unbindTooltip();
        this.safeRemoveLayer(marker.leafletMarker);
        marker.leafletMarker = null;
      }
      this.userMarkers.splice(index, 1);
    },
    
    panToMarker(marker) {
      if (this.map && marker.lat && marker.lng) {
        this.map.setView([marker.lat, marker.lng], this.map.getZoom());
      }
    },
    
    goToUserLocation() {
      if (this.userLocation) {
        this.map.setView([this.userLocation.lat, this.userLocation.lng], 8);
      } else if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const { latitude, longitude } = position.coords;
            this.userLocation = { lat: latitude, lng: longitude };
            this.map.setView([latitude, longitude], 8);
            this.addUserLocationMarker(latitude, longitude);
          }
        );
      }
    },
    getModelDisplayName(modelId) {
      return this.modelNames[modelId] || modelId;
    },
    
    getElementDisplayName(element) {
      return this.elementNames[element] || element;
    },
    
    onModelChange() {
      // Reset element to first available when model changes
      const elements = this.availableElements;
      this.newLayerElement = elements.length > 0 ? elements[0] : '';
    },
    
    addLayer() {
      if (!this.newLayerElement) return;
      
      const layer = {
        id: this.nextLayerId++,
        model: this.newLayerModel,
        element: this.newLayerElement,
        visible: true,
        opacity: 0.7,
        leafletLayer: null
      };
      
      this.activeLayers.push(layer);
      this.createLeafletLayer(layer);
      // Auto-expand new layer's legend
      this.expandedLegends[layer.id] = true;
      this.updateAllLegends();
      this.showAddLayerModal = false;
      // Restart animation if playing to include new layer
      this.restartAnimationIfPlaying();
    },
    
    removeLayer(index) {
      const layer = this.activeLayers[index];
      this.safeRemoveLayer(layer.leafletLayer);
      layer.leafletLayer = null;
      delete this.expandedLegends[layer.id];
      this.activeLayers.splice(index, 1);
      this.updateAllLegends();
      // Restart animation if playing to reflect removed layer
      this.restartAnimationIfPlaying();
    },
    
    toggleLayerVisibility(layer) {
      if (!this.map || !this.mapReady) return;
      
      if (layer.visible) {
        if (!layer.leafletLayer) {
          this.createLeafletLayer(layer);
        } else {
          layer.leafletLayer.addTo(this.map);
        }
        // Auto-expand when made visible
        this.expandedLegends[layer.id] = true;
      } else {
        // Remove and nullify the layer reference to avoid stale references
        this.safeRemoveLayer(layer.leafletLayer);
        layer.leafletLayer = null;
      }
      this.updateAllLegends();
      // Restart animation if playing to reflect visibility change
      this.restartAnimationIfPlaying();
    },
    
    toggleLegendExpand(layerId) {
      this.expandedLegends[layerId] = !this.expandedLegends[layerId];
    },
    
    createLeafletLayer(layer) {
      if (!this.map || !this.mapReady) return;
      
      // Don't attempt to load tiles without a valid API key
      if (!this.hasValidApiKey) {
        console.warn('Cannot load weather tiles: No API key available');
        return;
      }
      
      // Remove existing layer if present
      if (layer.leafletLayer) {
        this.safeRemoveLayer(layer.leafletLayer);
      }
      
      // Clear any previous errors for this layer
      delete this.layerErrors[layer.id];
      
      const tileUrl = this.buildTileUrl(layer.element, layer.model, this.currentTime);
      const self = this;
      
      layer.leafletLayer = L.tileLayer(tileUrl, {
        opacity: layer.opacity,
        pane: 'weatherPane',
        maxZoom: 18,
        attribution: '&copy; <a href="https://www.visualcrossing.com">Visual Crossing</a>'
      }).addTo(this.map);
      
      // Add error handler for tile loading failures
      layer.leafletLayer.on('tileerror', function(error) {
        self.handleTileError(error, layer);
      });
      
      // Track successful tile loads to clear errors if tiles start working
      layer.leafletLayer.on('tileload', function() {
        // If we had errors but now tiles are loading, clear the layer error
        if (self.layerErrors[layer.id]) {
          delete self.layerErrors[layer.id];
          // Recalculate total error count
          self.tileErrorCount = Object.values(self.layerErrors).reduce((sum, count) => sum + count, 0);
        }
      });
    },
    
    updateLayerOpacity(layer) {
      if (layer.leafletLayer) {
        layer.leafletLayer.setOpacity(layer.opacity);
      }
    },
    
    updateLayerTiles(layer, time) {
      if (!layer.leafletLayer || !this.map || !this.mapReady) return;
      
      const tileUrl = this.buildTileUrl(layer.element, layer.model, time);
      layer.leafletLayer.setUrl(tileUrl);
    },
    
    /**
     * Safely remove a Leaflet layer with proper cleanup.
     * Prevents errors when removing layers during animations or after map destruction.
     */
    safeRemoveLayer(layer) {
      if (!layer) return;
      
      try {
        // Remove any event listeners the layer might have
        layer.off();
        
        // Check if layer has a map reference and the map exists
        if (this.map && layer._map) {
          this.map.removeLayer(layer);
        }
      } catch (e) {
        // Silently handle errors during cleanup - layer may already be removed
        console.debug('Layer cleanup warning:', e.message);
      }
    },
    
    buildTileUrl(element, model, time, cacheBuster = null) {
      const sourceParam = model && model !== 'default' ? `&source=${model}` : '';
      const cacheBusterParam = cacheBuster ? `&_cb=${cacheBuster}` : '';
      return `${this.baseApiUrl}/VisualCrossingWebServices/rest/api/v1/map/tile/` +
             `${element}/{z}/{x}/{y}.webp` +
             `?key=${this.weatherApiKey}` +
             `&time=${this.formatFullDateTime(time)}` +
             `&unitGroup=${this.unitGroup}` +
             `&strict=${this.strictMode}` +
             `${sourceParam}${cacheBusterParam}`;
    },
    
    refreshAllLayers() {
      if (!this.map || !this.mapReady) return;
      
      this.activeLayers.forEach(layer => {
        if (layer.visible) {
          if (layer.leafletLayer) {
            // Layer exists, just update tiles
            this.updateLayerTiles(layer, this.currentTime);
          } else if (this.hasValidApiKey) {
            // Layer was added but not created (no API key at the time) - create it now
            this.createLeafletLayer(layer);
          }
        }
      });
      this.updateAllLegends();
    },
    
    changeBaseMap() {
      if (!this.map) return;
      
      this.safeRemoveLayer(this.baseLayer);
      this.baseLayer = null;
      
      const config = this.baseMaps[this.selectedBaseMap];
      this.baseLayer = L.tileLayer(config.url, {
        subdomains: 'abcd',
        maxZoom: 19,
        attribution: config.attribution
      }).addTo(this.map);
      
      if (this.showLabels) {
        this.addLabelsLayer();
      }
    },
    
    toggleLabels() {
      if (!this.map || !this.mapReady) return;
      
      if (this.showLabels) {
        this.addLabelsLayer();
      } else {
        this.safeRemoveLayer(this.labelLayer);
        this.labelLayer = null;
      }
    },
    
    toggleTileGrid() {
      if (!this.map || !this.mapReady) return;
      
      if (this.showTileGrid) {
        this.addTileGridLayer();
      } else {
        this.safeRemoveLayer(this.tileGridLayer);
        this.tileGridLayer = null;
      }
    },
    
    addTileGridLayer() {
      if (!this.map || !this.mapReady) return;
      
      this.safeRemoveLayer(this.tileGridLayer);
      this.tileGridLayer = null;
      
      // Create a dedicated pane for tile grid if it doesn't exist
      if (!this.map.getPane('tileGridPane')) {
        this.map.createPane('tileGridPane');
        this.map.getPane('tileGridPane').style.zIndex = 650;
        this.map.getPane('tileGridPane').style.pointerEvents = 'none';
      }
      
      // Create a custom GridLayer using canvas tiles
      const TileGridLayer = L.GridLayer.extend({
        createTile: function(coords) {
          const canvas = document.createElement('canvas');
          const tileSize = this.getTileSize();
          canvas.width = tileSize.x;
          canvas.height = tileSize.y;
          
          const ctx = canvas.getContext('2d');
          
          // Draw border (subtle)
          ctx.strokeStyle = 'rgba(128, 128, 128, 0.4)';
          ctx.lineWidth = 1;
          ctx.strokeRect(0.5, 0.5, tileSize.x - 1, tileSize.y - 1);
          
          // Draw label background
          const label = `z${coords.z} / ${coords.x} / ${coords.y}`;
          ctx.font = '11px monospace';
          const textWidth = ctx.measureText(label).width;
          
          ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
          ctx.fillRect(4, 4, textWidth + 12, 18);
          
          // Draw label text
          ctx.fillStyle = '#fff';
          ctx.fillText(label, 10, 17);
          
          return canvas;
        }
      });
      
      this.tileGridLayer = new TileGridLayer({
        pane: 'tileGridPane'
      }).addTo(this.map);
    },
    
    addLabelsLayer() {
      if (!this.map || !this.mapReady) return;
      
      this.safeRemoveLayer(this.labelLayer);
      this.labelLayer = null;
      
      const isDark = this.selectedBaseMap === 'carto-dark';
      const labelsUrl = isDark 
        ? 'https://{s}.basemaps.cartocdn.com/dark_only_labels/{z}/{x}/{y}{r}.png'
        : 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager_only_labels/{z}/{x}/{y}{r}.png';
      
      this.labelLayer = L.tileLayer(labelsUrl, {
        subdomains: 'abcd',
        maxZoom: 19,
        pane: 'labelsPane',
        attribution: '&copy; <a href="https://carto.com">CARTO</a>'
      }).addTo(this.map);
    },
    
    async updateAllLegends() {
      const visibleLayers = this.activeLayers.filter(l => l.visible && l.leafletLayer);
      
      if (visibleLayers.length === 0) {
        this.visibleLayerLegends = [];
        return;
      }
      
      const legendPromises = visibleLayers.map(async (layer) => {
        const legendUrl = `${this.baseApiUrl}/VisualCrossingWebServices/rest/api/v1/map/legend/` +
                          `${layer.element}?key=${this.weatherApiKey}&unitGroup=${this.unitGroup}`;
        
        try {
          const response = await fetch(legendUrl);
          const raw = await response.json();

          // Handle both single array of bins and array of arrays of bins
          let binGroups;
          if (Array.isArray(raw) && raw.length > 0 && Array.isArray(raw[0])) {
            // Response is array of arrays - keep as separate groups
            binGroups = raw;
          } else {
            // Single array - wrap in array for consistent handling
            binGroups = [raw];
          }
          
          // Cache flattened bins for color lookups
          this.elementBinCache[layer.element] = binGroups.flat();
          
          let html = '';
          binGroups.forEach((bins, groupIndex) => {
            if (groupIndex > 0) {
              html += '<div style="margin-top:8px;border-top:1px solid #eee;padding-top:8px;"></div>';
            }
            bins.forEach((bin, index) => {
              if (!bin || !bin.color) return;
              const hasLabel = !!bin.label;
              const label = bin.label || (bin.min ?? '');
              
              if (hasLabel) {
                const textColor = this.getContrastingTextColor(bin.color);
                html += `<div style="display:inline-flex;align-items:center;margin:2px 4px 2px 0;background:${bin.color};color:${textColor};padding:2px 8px;border-radius:10px;font-size:11px;font-weight:500;">${label}</div>`;
              } else {
                html += `<div style="display:inline-block;text-align:center;margin-right:5px;vertical-align:top;">
                          <div style="background-color:${bin.color};width:20px;height:20px;"></div>
                          ${index % Math.max(1, Math.ceil(bins.length / 10)) === 0 ? `<span style="font-size:10px;">${label}</span>` : ''}
                        </div>`;
              }
              
            });
          });
          
          return {
            layerId: layer.id,
            title: `${this.getModelDisplayName(layer.model)} - ${this.getElementDisplayName(layer.element)}`,
            html: html
          };
        } catch (error) {
          console.error('Error loading legend for', layer.element, error);
          return null;
        }
      });
      
      const results = await Promise.all(legendPromises);
      this.visibleLayerLegends = results.filter(r => r !== null);
    },
    
    toggleGeoJsonLayer() {
      if (!this.map || !this.mapReady) return;
      
      if (this.showGeoJson) {
        if (!this.geoJsonLayer) {
          this.addGeoJsonTileLayer();
        } else {
          const pane = this.map.getPane('citiesPane');
          if (pane) pane.style.display = '';
          this.refreshGeoJsonTiles();
        }
      } else {
        // Abort pending requests first
        this.geoJsonAbortControllers.forEach(ctrl => ctrl.abort());
        this.geoJsonAbortControllers.clear();
        
        // Clean up tile groups
        Object.keys(this.geoJsonTiles).forEach(key => {
          this.safeRemoveLayer(this.geoJsonTiles[key]);
        });
        this.geoJsonTiles = {};
        
        // Remove and destroy the GeoJSON layer completely to prevent stale animation handlers
        if (this.geoJsonLayer) {
          this.geoJsonLayer.off();
          if (this.geoJsonLayer._map) {
            this.map.removeLayer(this.geoJsonLayer);
          }
          this.geoJsonLayer = null;
        }
      }
    },
    
     addGeoJsonTileLayer() {
      if (!this.map || !this.mapReady || this.geoJsonLayer) return;
      
      const self = this;
      const GeoJSONGridLayer = L.GridLayer.extend({
        createTile: function(coords, done) {
          const tile = document.createElement('div');
          const key = `${coords.z}/${coords.x}/${coords.y}`;
          
          // Check if map is still valid
          if (!self.map || !self.mapReady) {
            done(null, tile);
            return tile;
          }
          
          if (self.geoJsonAbortControllers.has(key)) {
            self.geoJsonAbortControllers.get(key).abort();
          }
          
          if (self.geoJsonTiles[key]) {
            done(null, tile);
            return tile;
          }
          
          const controller = new AbortController();
          self.geoJsonAbortControllers.set(key, controller);
          
          const url = `${self.baseApiUrl}/VisualCrossingWebServices/rest/api/v1/map/tile/` +
                      `current/${coords.z}/${coords.x}/${coords.y}.geojson` +
                      `?key=${self.weatherApiKey}&unitGroup=${self.unitGroup}&strict=${self.strictMode}`;
          
          fetch(url, { signal: controller.signal })
            .then(r => r.json())
            .then(fc => {
              // Double-check map validity before adding layers
              if (!fc || !fc.features || !self.showGeoJson || !self.map || !self.mapReady) return;
              
              if (self.geoJsonTiles[key]) {
                self.safeRemoveLayer(self.geoJsonTiles[key]);
              }
              
              const tileGroup = L.layerGroup();
              
                            fc.features.forEach(feature => {
                if (feature.geometry.type !== 'Point') return;
                
                const [lng, lat] = feature.geometry.coordinates;
                const props = feature.properties || {};
                const cc = props.data?.currentConditions || {};
                
                // Get display value based on selected element
                const displayInfo = self.getGeoJsonDisplayInfo(cc);
                if (!displayInfo) return;
                
                const cityName = props.resolvedAddress || `${lat.toFixed(2)},${lng.toFixed(2)}`;
                
                // Build tooltip text
                const titleText = `${cityName}\nTemp: ${cc.temp ?? 'N/A'}°\nPrecip: ${cc.precip ?? 'N/A'}\nWind: ${cc.windspeed ?? 'N/A'} ${self.getWindDirectionLabel(cc.winddir)}`;
                
                let markerHtml;
                if (displayInfo.isWind) {
                  // Wind marker with arrow
                  const arrowRotation = displayInfo.winddir; // Arrow points in direction wind is going TO
                  markerHtml = `
                    <div class="wind-marker" title="${titleText.replace(/"/g, '&quot;')}" style="position:relative;width:44px;height:44px;">
                      <div style="position:absolute;top:8px;left:8px;background:${displayInfo.fill};color:${displayInfo.textColor};width:28px;height:28px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:500;">${displayInfo.label}</div>
                      <svg style="position:absolute;top:0;left:0;width:44px;height:44px;transform:rotate(${arrowRotation}deg);" viewBox="0 0 44 44">
                        <path d="M22 1 L27 10 L22 7 L17 10 Z" fill="${displayInfo.textColor}" stroke="${displayInfo.fill}" stroke-width="1"/>
                      </svg>
                    </div>`;
                } else {
                  markerHtml = `<div class="circle-marker" title="${titleText.replace(/"/g, '&quot;')}" style="background:${displayInfo.fill};color:${displayInfo.textColor};width:${displayInfo.width}px;height:28px;border-radius:14px;display:flex;align-items:center;justify-content:center;text-align:center;cursor:pointer;font-size:11px;padding:0 4px;white-space:nowrap;">${displayInfo.label}</div>`;
                }
                
                const icon = L.divIcon({
                  className: 'city-marker-icon',
                  html: markerHtml,
                  iconSize: displayInfo.isWind ? [44, 44] : [displayInfo.width, 28],
                  iconAnchor: displayInfo.isWind ? [22, 22] : [displayInfo.width / 2, 14]
                });
                
                const marker = L.marker([lat, lng], { icon, pane: 'citiesPane', interactive: false });
                
                tileGroup.addLayer(marker);
              });
              
              self.geoJsonTiles[key] = tileGroup;
              if (self.map && self.mapReady && self.showGeoJson) {
                tileGroup.addTo(self.map);
              }
              done(null, tile);
            })
            .catch(err => {
              if (err.name !== 'AbortError') console.warn('GeoJSON tile error:', err);
              done(err, tile);
            })
            .finally(() => {
              self.geoJsonAbortControllers.delete(key);
            });
          
          return tile;
        },
        
        _removeTile: function(key) {
          // Clean up markers when tile is removed
          // Check if tiles object still exists (may be null during destruction)
          const tileData = this._tiles ? this._tiles[key] : null;
          const coords = tileData?.coords;
          if (coords) {
            const tileKey = `${coords.z}/${coords.x}/${coords.y}`;
            if (self.geoJsonTiles[tileKey]) {
              self.safeRemoveLayer(self.geoJsonTiles[tileKey]);
              delete self.geoJsonTiles[tileKey];
            }
            if (self.geoJsonAbortControllers.has(tileKey)) {
              self.geoJsonAbortControllers.get(tileKey).abort();
              self.geoJsonAbortControllers.delete(tileKey);
            }
          }
          // Only call parent if layer still has map reference
          if (this._map) {
            L.GridLayer.prototype._removeTile.call(this, key);
          }
        }
        // Note: Animation method overrides removed - now handled by global L.GridLayer prototype patches
      });
      
      this.geoJsonLayer = new GeoJSONGridLayer({
        minZoom: 5,
        maxZoom: 18,
        updateWhenIdle: true,
        updateWhenZooming: false,
        keepBuffer: 2
      });
      
      this.geoJsonLayer.addTo(this.map);
    },

    getGeoJsonDisplayInfo(cc) {
      const element = this.geoJsonElement;
      
      if (element === 'temp') {
        const temp = cc.temp;
        if (temp == null) return null;
        const fill = this.getColor(temp, 'temp');
        const textColor = this.getContrastingTextColor(fill);
        return { label: Math.round(temp) + '°', fill, textColor, width: 28, isWind: false };
      } 
      else if (element === 'precip') {
        const precip = cc.precip;
        if (precip == null) return null;
        // Use a blue gradient for precip
        const fill = precip > 0 ? this.getPrecipColor(precip) : '#e0e0e0';
        const textColor = this.getContrastingTextColor(fill);
        return { label: precip.toFixed(1), fill, textColor, width: 36, isWind: false };
      } 
      else if (element === 'wind') {
        const windspeed = cc.windspeed;
        const winddir = cc.winddir;
        if (windspeed == null) return null;
        const fill = this.getWindColor(windspeed);
        const textColor = this.getContrastingTextColor(fill);
        return { 
          label: Math.round(windspeed), 
          fill, 
          textColor, 
          width: 36, 
          isWind: true,
          winddir: winddir ?? 0
        };
      }
      
      return null;
    },
    
    getWindDirectionLabel(degrees) {
      if (degrees == null) return '';
      const directions = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
      const index = Math.round(degrees / 45) % 8;
      return directions[index];
    },
    
    getPrecipColor(precip) {
      // Simple precipitation color scale
      if (precip <= 0) return '#e0e0e0';
      if (precip < 0.5) return '#a8d5e5';
      if (precip < 2) return '#5fb3d4';
      if (precip < 5) return '#3498db';
      if (precip < 10) return '#2471a3';
      return '#1a4971';
    },
    
    getWindColor(windspeed) {
      // Wind speed color scale (assuming metric m/s or us mph based on unitGroup)
      if (windspeed < 5) return '#c8e6c9';
      if (windspeed < 10) return '#81c784';
      if (windspeed < 15) return '#ffd54f';
      if (windspeed < 25) return '#ff9800';
      if (windspeed < 40) return '#f44336';
      return '#9c27b0';
    },
    refreshGeoJsonTiles() {
      if (!this.geoJsonLayer || !this.showGeoJson || !this.map || !this.mapReady) return;
      
      Object.keys(this.geoJsonTiles).forEach(key => {
        this.safeRemoveLayer(this.geoJsonTiles[key]);
      });
      this.geoJsonTiles = {};
      this.geoJsonAbortControllers.forEach(ctrl => ctrl.abort());
      this.geoJsonAbortControllers.clear();
      this.geoJsonLayer.redraw();
    },
    
    toggleMoviePlay() {
      if (this.isPlaying) {
        this.stopMovie();
      } else {
        this.startMovie(); // async but we don't need to wait
      }
    },
    
    /**
     * Toggle the timeline panel visibility.
     * Generates time keys if they haven't been generated yet.
     */
    toggleTimelinePanel() {
      this.showTimelinePanel = !this.showTimelinePanel;
      // If showing timeline and no time keys yet, generate them
      if (this.showTimelinePanel && this.animationTimeKeys.length === 0) {
        this.animationTimeKeys = this.generateAnimationTimeKeys();
      }
    },
    
    /**
     * Reset the custom loop steps to use auto-calculated values
     */
    resetLoopWindow() {
      this.customStepsBack = null;
      this.customStepsForward = null;
    },
    
    /**
     * Start the animation with double-buffered frame preloading.
     * Routes to either canvas-based or tile-layer-based animation.
     */
    async startMovie() {
      // console.log('startMovie called, useCanvasAnimation:', this.useCanvasAnimation);
      
      // If already preloading, don't start another
      if (this.isPreloadingFrames) {
        // console.log('Already preloading, returning');
        return;
      }
      
      // Stop any existing animation first
      if (this.movieInterval) {
        clearInterval(this.movieInterval);
        this.movieInterval = null;
      }
      if (this.animationFrameRequestId) {
        cancelAnimationFrame(this.animationFrameRequestId);
        this.animationFrameRequestId = null;
      }
      
      // Route to appropriate animation mode
      if (this.useCanvasAnimation) {
        // console.log('Using canvas animation mode');
        await this.startCanvasMovie();
      } else {
        // console.log('Using tile layer animation mode');
        await this.startTileLayerMovie();
      }
    },
    
    /**
     * Start animation using TRUE double-buffering.
     * Uses only 2 tile layers per element (front + back buffers).
     * Preloads next frame in back buffer while displaying front buffer.
     * Swaps buffers when back buffer is loaded.
     */
    async startTileLayerMovie() {
      // console.log('startTileLayerMovie called');
      
      // Generate time keys for animation
      this.animationTimeKeys = this.generateAnimationTimeKeys();
      // console.log('Generated time keys:', this.animationTimeKeys.length);
      
      if (this.animationTimeKeys.length === 0) {
        // console.log('No time keys, falling back to simple movie');
        this.startSimpleMovie();
        return;
      }
      
      // Show preload indicator
      this.isPreloadingFrames = true;
      this.preloadProgress = 0;
      
      const visibleLayers = this.activeLayers.filter(layer => layer.visible);
      // console.log('Visible layers:', visibleLayers.length);
      
      if (visibleLayers.length === 0) {
        // console.log('No visible layers');
        this.isPreloadingFrames = false;
        this.startSimpleMovie();
        return;
      }
      
      // Initialize double buffers (this loads the first two frames)
      this.preloadProgress = 10;
      // console.log('Initializing double buffers...');
      await this.initializeDoubleBuffers();
      // console.log('Double buffers initialized, animationBuffers:', Object.keys(this.animationBuffers).length);
      
      if (!this.isPreloadingFrames) {
        // console.log('Preloading was cancelled');
        return; // Cancelled
      }
      
      this.preloadProgress = 100;
      this.isPreloadingFrames = false;
      this.isPlaying = true;
      this.animationFrameIndex = this.findCurrentFrameIndex();
      // console.log('Starting playback at frame index:', this.animationFrameIndex);
      
      // Show the front buffer
      this.showBuffer('front');
      
      // Mark next frame as ready (back buffer is already loaded)
      this.nextFrameReady = true;
      
      // Start the animation loop
      this.movieInterval = setInterval(() => {
        this.advanceDoubleBufferFrame();
      }, 1000 / this.playbackSpeed);
      
      // console.log('Animation started with interval');
    },
    
    /**
     * Start animation using canvas rasterization (lower memory approach).
     * Renders each frame to a canvas image and animates via canvas drawing.
     */
    async startCanvasMovie() {
      // Preload and rasterize frames
      await this.preloadCanvasFrames();
      
      // If preload was cancelled or no frames, fall back to simple animation
      if (this.canvasFrameCache.length === 0) {
        this.startSimpleMovie();
        return;
      }
      
      this.isPlaying = true;
      this.animationFrameIndex = this.findCurrentFrameIndex();
      
      // Show the canvas overlay
      this.showCanvasOverlay();
      
      // Show the current frame
      this.showCanvasFrame(this.animationFrameIndex, 0);
      
      // Use requestAnimationFrame with crossfade for smooth animation
      this.startCrossfadeLoop();
    },
    
    /**
     * Fallback simple animation without double-buffering (for when preload fails)
     */
    startSimpleMovie() {
      // Clear any existing interval
      if (this.movieInterval) {
        clearInterval(this.movieInterval);
        this.movieInterval = null;
      }
      
      this.isPlaying = true;
      this.movieInterval = setInterval(() => {
        this.incrementTime();
      }, 1000 / this.playbackSpeed);
    },
    
    stopMovie() {
      this.isPlaying = false;
      this.isPreloadingFrames = false;
      this.preloadProgress = 0;
      this.nextFrameReady = false;
      
      if (this.movieInterval) {
        clearInterval(this.movieInterval);
        this.movieInterval = null;
      }
      
      if (this.animationFrameRequestId) {
        cancelAnimationFrame(this.animationFrameRequestId);
        this.animationFrameRequestId = null;
      }
      
      // Restore main layers and clear animation caches
      this.restoreMainLayers();
      this.clearDoubleBuffers();
      this.clearCanvasCache();
      this.hideCanvasOverlay();
    },
    
    /**
     * Initialize double buffers (front + back) for each visible layer.
     * Front buffer is loaded with the starting frame, back with the next frame.
     */
    async initializeDoubleBuffers() {
      // console.log('initializeDoubleBuffers: starting');
      
      // Don't attempt to load tiles without a valid API key
      if (!this.hasValidApiKey) {
        console.warn('Cannot initialize animation buffers: No API key available');
        return;
      }
      
      this.clearDoubleBuffers();
      
      const visibleLayers = this.activeLayers.filter(layer => layer.visible);
      // console.log('initializeDoubleBuffers: visibleLayers count:', visibleLayers.length);
      
      if (visibleLayers.length === 0 || this.animationTimeKeys.length === 0) {
        // console.log('initializeDoubleBuffers: early return - no layers or no time keys');
        return;
      }
      
      // Get starting frame indices
      const frontIndex = this.findCurrentFrameIndex();
      const backIndex = (frontIndex + 1) % this.animationTimeKeys.length;
      
      const frontTimeKey = this.animationTimeKeys[frontIndex];
      const backTimeKey = this.animationTimeKeys[backIndex];
      // console.log('initializeDoubleBuffers: frontIndex:', frontIndex, 'backIndex:', backIndex);
      
      const loadPromises = [];
      
      for (const layer of visibleLayers) {
        // console.log('initializeDoubleBuffers: processing layer:', layer.id, layer.element, layer.model);
        // Build URLs for front and back buffers
        const frontUrl = this.buildTileUrl(layer.element, layer.model, new Date(frontTimeKey));
        const backUrl = this.buildTileUrl(layer.element, layer.model, new Date(backTimeKey));
        // console.log('initializeDoubleBuffers: frontUrl:', frontUrl.substring(0, 100) + '...');
        
        // Create front buffer layer with actual URL (starts invisible)
        const frontLayer = L.tileLayer(frontUrl, {
          opacity: 0,
          pane: 'weatherPane',
          maxZoom: 18,
          attribution: ''
        }).addTo(this.map);
        
        // Create back buffer layer with actual URL (starts invisible)
        const backLayer = L.tileLayer(backUrl, {
          opacity: 0,
          pane: 'weatherPane',
          maxZoom: 18,
          attribution: ''
        }).addTo(this.map);
        
        this.animationBuffers[layer.id] = {
          front: frontLayer,
          back: backLayer,
          frontTimeKey: frontTimeKey,
          backTimeKey: backTimeKey,
          targetOpacity: layer.opacity
        };
        // console.log('initializeDoubleBuffers: created buffer for layer:', layer.id, 'animationBuffers keys:', Object.keys(this.animationBuffers));
        
        // Wait for front layer to load
        const frontPromise = new Promise((resolve) => {
          let resolved = false;
          const onLoad = () => {
            if (!resolved) {
              resolved = true;
              frontLayer.off('load', onLoad);
              resolve();
            }
          };
          frontLayer.on('load', onLoad);
          setTimeout(onLoad, 3000);
        });
        loadPromises.push(frontPromise);
        
        // Wait for back layer to load  
        const backPromise = new Promise((resolve) => {
          let resolved = false;
          const onLoad = () => {
            if (!resolved) {
              resolved = true;
              backLayer.off('load', onLoad);
              resolve();
            }
          };
          backLayer.on('load', onLoad);
          setTimeout(onLoad, 3000);
        });
        loadPromises.push(backPromise);
        
        // Hide the main layer during animation
        if (layer.leafletLayer) {
          layer.leafletLayer.setOpacity(0);
        }
      }
      
      // Wait for all buffers to load
      await Promise.all(loadPromises);
    },
    
    /**
     * Load a specific frame into a buffer (front or back).
     * Returns a promise that resolves when the tiles are loaded.
     */
    async loadFrameToBuffer(bufferName, frameIndex) {
      if (frameIndex < 0 || frameIndex >= this.animationTimeKeys.length) return;
      
      const timeKey = this.animationTimeKeys[frameIndex];
      const visibleLayers = this.activeLayers.filter(layer => layer.visible);
      const loadPromises = [];
      
      for (const layer of visibleLayers) {
        const buffer = this.animationBuffers[layer.id];
        if (!buffer) continue;
        
        const bufferLayer = buffer[bufferName];
        if (!bufferLayer) continue;
        
        // Check if this buffer already has this frame loaded
        if (buffer[bufferName + 'TimeKey'] === timeKey) {
          continue; // Already loaded
        }
        
        // Build URL for this frame
        const tileUrl = this.buildTileUrl(layer.element, layer.model, new Date(timeKey));
        
        // Create load promise BEFORE calling setUrl to avoid race condition
        const loadPromise = new Promise((resolve) => {
          let resolved = false;
          const onLoad = () => {
            if (!resolved) {
              resolved = true;
              bufferLayer.off('load', onLoad);
              resolve();
            }
          };
          // Attach listener BEFORE triggering load
          bufferLayer.on('load', onLoad);
          // Timeout after 2 seconds
          setTimeout(onLoad, 2000);
        });
        
        loadPromises.push(loadPromise);
        
        // Now trigger the tile loading
        bufferLayer.setUrl(tileUrl);
        buffer[bufferName + 'TimeKey'] = timeKey;
      }
      
      if (loadPromises.length > 0) {
        await Promise.all(loadPromises);
      }
    },
    
    /**
     * Show a specific buffer (front or back), hiding the other.
     */
    showBuffer(bufferName) {
      const otherBuffer = bufferName === 'front' ? 'back' : 'front';
      
      for (const layerId in this.animationBuffers) {
        const buffer = this.animationBuffers[layerId];
        if (!buffer) continue;
        
        // Show the active buffer with its opacity
        if (buffer[bufferName]) {
          buffer[bufferName].setOpacity(buffer.targetOpacity);
        }
        
        // Hide the other buffer
        if (buffer[otherBuffer]) {
          buffer[otherBuffer].setOpacity(0);
        }
      }
      
      // Update currentTime based on which buffer is showing
      const timeKey = this.animationBuffers[Object.keys(this.animationBuffers)[0]]?.[bufferName + 'TimeKey'];
      if (timeKey) {
        this.currentTime = new Date(timeKey);
        // Note: Don't call updateAllLegends() here - legends don't change during animation
      }
    },

    /**
     * Preload the next frame in the back buffer.
     */
    async preloadNextFrame() {
      if (!this.isPlaying) return;
      
      const nextIndex = (this.animationFrameIndex + 1) % this.animationTimeKeys.length;
      
      // Determine which buffer is currently back (not showing)
      // We alternate: if front is showing, load into back
      await this.loadFrameToBuffer('back', nextIndex);
      this.nextFrameReady = true;
    },
    
    /**
     * Advance to next frame using double-buffer swap.
     */
    advanceDoubleBufferFrame() {
      if (!this.isPlaying) return;
      
      // Advance frame index
      this.animationFrameIndex = (this.animationFrameIndex + 1) % this.animationTimeKeys.length;
      
      // Swap buffers: back becomes front, front becomes back
      this.swapBuffers();
      
      // Show the new front buffer
      this.showBuffer('front');
      
      // Start preloading the next frame into the back buffer
      this.nextFrameReady = false;
      this.preloadNextFrame();
    },
    
    /**
     * Swap front and back buffers.
     */
    swapBuffers() {
      for (const layerId in this.animationBuffers) {
        const buffer = this.animationBuffers[layerId];
        if (!buffer) continue;
        
        // Swap the layer references
        const tempLayer = buffer.front;
        buffer.front = buffer.back;
        buffer.back = tempLayer;
        
        // Swap the time keys
        const tempTimeKey = buffer.frontTimeKey;
        buffer.frontTimeKey = buffer.backTimeKey;
        buffer.backTimeKey = tempTimeKey;
      }
    },
    
    /**
     * Clear double buffers and remove buffer layers from map.
     */
    clearDoubleBuffers() {
      for (const layerId in this.animationBuffers) {
        const buffer = this.animationBuffers[layerId];
        if (!buffer) continue;
        
        this.safeRemoveLayer(buffer.front);
        this.safeRemoveLayer(buffer.back);
      }
      
      this.animationBuffers = {};
      // NOTE: Don't clear animationTimeKeys here - they are managed by startTileLayerMovie
      this.animationFrameIndex = 0;
      this.nextFrameReady = false;
    },
    
    /**
     * Generate the list of time keys for the animation loop.
     * Returns array of ISO time strings for each frame.
     * Uses step-based window: stepsBack from current time, stepsForward from current time.
     */
    generateAnimationTimeKeys() {
      const { stepsBack, stepsForward } = this.effectiveLoopSteps;
      const centerTime = this.currentTime;
      const timeSkip = this.currentTimeSkip;
      
      const timeKeys = [];
      
      // Generate steps back from center time
      for (let i = stepsBack; i > 0; i--) {
        const t = new Date(centerTime);
        t.setUTCMinutes(t.getUTCMinutes() - (i * timeSkip));
        timeKeys.push(t.toISOString());
      }
      
      // Add center time
      timeKeys.push(centerTime.toISOString());
      
      // Generate steps forward from center time
      for (let i = 1; i <= stepsForward; i++) {
        const t = new Date(centerTime);
        t.setUTCMinutes(t.getUTCMinutes() + (i * timeSkip));
        timeKeys.push(t.toISOString());
      }
      
      // Limit to max frames
      return timeKeys.slice(0, this.maxAnimationFrames);
    },
    
    /**
     * Find the frame index closest to the current time.
     */
    findCurrentFrameIndex() {
      if (this.animationTimeKeys.length === 0) return 0;
      
      const currentIso = this.currentTime.toISOString();
      const idx = this.animationTimeKeys.indexOf(currentIso);
      return idx >= 0 ? idx : 0;
    },
    
    /**
     * Restore the main layers after animation stops.
     */
    restoreMainLayers() {
      const visibleLayers = this.activeLayers.filter(layer => layer.visible);
      
      for (const layer of visibleLayers) {
        // Restore main layer opacity
        if (layer.leafletLayer) {
          layer.leafletLayer.setOpacity(layer.opacity);
        }
        
        // Update main layer to current time
        this.updateLayerTiles(layer, this.currentTime);
      }
    },
    
    /**
     * Restart animation if currently playing.
     * Used when settings change that require regenerating frames (loop window, time skip, etc.)
     * Uses debouncing to avoid rapid restarts during slider drags.
     */
    async restartAnimationIfPlaying() {
      if (this.isPlaying || this.isPreloadingFrames) {
        // Stop current animation immediately
        this.stopMovie();
        
        // Clear any pending restart
        if (this.animationRestartTimer) {
          clearTimeout(this.animationRestartTimer);
        }
        
        // Debounce the restart (wait 300ms for user to finish adjusting sliders)
        this.animationRestartTimer = setTimeout(async () => {
          await this.startMovie();
        }, 300);
      }
    },
    
    /**
     * Handle map movement (pan/zoom) during animation.
     * Stops animation because tiles need to be reloaded for new viewport.
     */
    handleMapMoveForAnimation() {
      // Always clear canvas cache when map moves - cached frames are for the old position
      // and would be misaligned if used after a pan/zoom
      this.clearCanvasCache();
      
      if (this.isPlaying || this.isPreloadingFrames) {
        // Clear any pending restart timer
        if (this.animationRestartTimer) {
          clearTimeout(this.animationRestartTimer);
          this.animationRestartTimer = null;
        }
        // Stop animation - user is interacting with the map
        this.stopMovie();
      }
    },

    adjustPlaybackSpeed() {
      if (this.isPlaying) {
        // Just restart the interval with the new speed - don't re-preload frames
        if (this.movieInterval) {
          clearInterval(this.movieInterval);
        }
        
        // Use the appropriate advance method based on animation mode
        if (this.useCanvasAnimation) {
          this.movieInterval = setInterval(() => {
            this.advanceCanvasFrame();
          }, 1000 / this.playbackSpeed);
        } else {
          this.movieInterval = setInterval(() => {
            this.advanceDoubleBufferFrame();
          }, 1000 / this.playbackSpeed);
        }
      }
    },
    
    // ========================================
    // Canvas-based Animation Methods
    // ========================================
    
    /**
     * Preload frames by rasterizing tile layers to canvas images.
     * More memory-efficient than keeping multiple L.tileLayer objects.
     */
    async preloadCanvasFrames() {
      // console.log('preloadCanvasFrames: starting');
      if (!this.map || !this.mapReady) {
        // console.log('preloadCanvasFrames: map not ready');
        return;
      }
      
      // Don't attempt to load tiles without a valid API key
      if (!this.hasValidApiKey) {
        console.warn('Cannot preload animation frames: No API key available');
        return;
      }
      
      this.isPreloadingFrames = true;
      this.preloadProgress = 0;
      this.canvasFrameCache = [];
      
      // Generate time keys for animation
      this.animationTimeKeys = this.generateAnimationTimeKeys();
      
      if (this.animationTimeKeys.length === 0) {
        this.isPreloadingFrames = false;
        return;
      }
      
      const visibleLayers = this.activeLayers.filter(layer => layer.visible);
      if (visibleLayers.length === 0) {
        this.isPreloadingFrames = false;
        return;
      }
      
      const totalFrames = this.animationTimeKeys.length;
      
      // Get map container dimensions
      const mapContainer = this.map.getContainer();
      const width = mapContainer.offsetWidth;
      const height = mapContainer.offsetHeight;
      
      // Create temporary canvas for rasterization
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = width;
      tempCanvas.height = height;
      const ctx = tempCanvas.getContext('2d');
      
      // For each time key, create temporary tile layers, wait for load, rasterize, then remove
      for (let i = 0; i < this.animationTimeKeys.length; i++) {
        if (!this.isPreloadingFrames) return; // Check for cancellation
        
        const timeKey = this.animationTimeKeys[i];
        const frameTime = new Date(timeKey);
        
        // Create temporary tile layers for this frame
        const tempLayers = [];
        const loadPromises = [];
        
        // Use a cache-buster to force fresh CORS requests (avoids tainted canvas from cached non-CORS tiles)
        const cacheBuster = `cors_${Date.now()}_${i}`;
        
        // Hide existing layers to prevent their tiles from tainting the canvas
        const hiddenLayers = [];
        for (const layer of this.activeLayers) {
          if (layer.leafletLayer && layer.visible) {
            layer.leafletLayer.setOpacity(0);
            hiddenLayers.push(layer);
          }
        }
        
        for (const layer of visibleLayers) {
          const tileUrl = this.buildTileUrl(layer.element, layer.model, frameTime, cacheBuster);
          
          const tempLayer = L.tileLayer(tileUrl, {
            opacity: layer.opacity,
            pane: 'weatherPane',
            maxZoom: 18,
            attribution: '',
            crossOrigin: 'anonymous' // Required for canvas rasterization
          }).addTo(this.map);
          
          tempLayers.push({ layer: tempLayer, opacity: layer.opacity });
          
          // Wait for tiles to load
          const loadPromise = new Promise((resolve) => {
            let resolved = false;
            const onLoad = () => {
              if (!resolved) {
                resolved = true;
                resolve();
              }
            };
            tempLayer.on('load', onLoad);
            setTimeout(onLoad, 3000); // Timeout after 3s
          });
          
          loadPromises.push(loadPromise);
        }
        
        // Wait for all layers of this frame to load
        await Promise.all(loadPromises);
        
        if (!this.isPreloadingFrames) {
          // Cancelled - clean up temp layers
          tempLayers.forEach(({ layer }) => this.safeRemoveLayer(layer));
          return;
        }
        
        // Small delay to ensure tiles are rendered
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Rasterize the weather pane to canvas
        try {
          // console.log('preloadCanvasFrames: rasterizing frame', i, 'of', this.animationTimeKeys.length);
          await this.rasterizeWeatherPane(ctx, width, height, tempLayers);
          
          // Store the frame as a data URL and preload the Image object
          // console.log('preloadCanvasFrames: calling toDataURL');
          const imageData = tempCanvas.toDataURL('image/png');
          // console.log('preloadCanvasFrames: got imageData, length:', imageData.length);
          
          // Preload the image for faster playback
          const img = new Image();
          img.src = imageData;
          this.canvasFrameCache.push({ timeKey, imageData, img });
        } catch (err) {
          console.error('Failed to rasterize frame:', timeKey, err.message, err);
        }
        
        // Remove temporary layers
        tempLayers.forEach(({ layer }) => this.safeRemoveLayer(layer));
        
        // Restore hidden layers
        for (const layer of hiddenLayers) {
          if (layer.leafletLayer) {
            layer.leafletLayer.setOpacity(layer.opacity);
          }
        }
        
        // Update progress
        this.preloadProgress = Math.round(((i + 1) / totalFrames) * 100);
      }
      
      if (!this.isPreloadingFrames) return;
      
      this.preloadProgress = 100;
      this.isPreloadingFrames = false;
    },
    
    /**
     * Rasterize the weather tile layers to a canvas.
     * Uses the actual tile images from the DOM.
     * Only draws tiles with crossOrigin='anonymous' to avoid tainting the canvas.
     */
    async rasterizeWeatherPane(ctx, width, height, tempLayers) {
      // Clear canvas
      ctx.clearRect(0, 0, width, height);
      
      // Get the weather pane element
      const weatherPane = this.map.getPane('weatherPane');
      if (!weatherPane) return;
      
      // Get the map container's bounding rect as our reference point
      const mapContainer = this.map.getContainer();
      const containerRect = mapContainer.getBoundingClientRect();
      
      // Build a map of layer opacities from tempLayers
      // tempLayers contains { layer, opacity } objects
      const layerOpacities = new Map();
      for (const { layer, opacity } of tempLayers) {
        const container = layer.getContainer();
        if (container) {
          layerOpacities.set(container, opacity);
        }
      }
      
      // Get all tile containers within the weather pane
      const tileContainers = weatherPane.querySelectorAll('.leaflet-tile-container');
      
      let drawnCount = 0;
      let skippedCount = 0;
      
      for (const container of tileContainers) {
        // Get the layer container (parent of tile container)
        const layerContainer = container.closest('.leaflet-layer');
        
        // Check if this container belongs to one of our temp layers
        let layerOpacity = 1;
        if (layerContainer && layerOpacities.has(layerContainer)) {
          layerOpacity = layerOpacities.get(layerContainer);
        }
        
        const tiles = container.querySelectorAll('img.leaflet-tile');
        
        for (const tile of tiles) {
          if (!tile.complete || tile.naturalWidth === 0) continue;
          
          // CRITICAL: Only draw tiles with crossOrigin set to avoid tainting the canvas
          // Tiles from original layers (without CORS) will be skipped
          if (tile.crossOrigin !== 'anonymous') {
            skippedCount++;
            continue;
          }
          
          // Get tile position using getBoundingClientRect for accurate screen position
          // This accounts for all transforms in the hierarchy (pane, container, tile)
          const tileRect = tile.getBoundingClientRect();
          
          // Calculate position relative to the map container
          const x = tileRect.left - containerRect.left;
          const y = tileRect.top - containerRect.top;
          
          // Use the layer opacity from our tempLayers, not from DOM
          // (Leaflet applies opacity to container, not individual tiles)
          ctx.globalAlpha = layerOpacity;
          try {
            ctx.drawImage(tile, x, y);
            drawnCount++;
          } catch (err) {
            console.error('rasterizeWeatherPane: drawImage failed:', err.message);
          }
        }
      }
      
      // console.log('rasterizeWeatherPane: drew', drawnCount, 'tiles, skipped', skippedCount, 'non-CORS tiles');
      ctx.globalAlpha = 1;
    },
    
    /**
     * Create and show the animation canvas overlay.
     */
    showCanvasOverlay() {
      if (this.animationCanvas) return;
      
      const mapContainer = this.map.getContainer();
      const canvas = document.createElement('canvas');
      canvas.id = 'animation-canvas-overlay';
      canvas.width = mapContainer.offsetWidth;
      canvas.height = mapContainer.offsetHeight;
      canvas.style.cssText = `
        position: absolute;
        top: 0;
        left: 0;
        z-index: 450;
        pointer-events: none;
      `;
      
      mapContainer.appendChild(canvas);
      this.animationCanvas = canvas;
      
      // Hide the main weather layers during canvas animation
      const visibleLayers = this.activeLayers.filter(layer => layer.visible);
      for (const layer of visibleLayers) {
        if (layer.leafletLayer) {
          layer.leafletLayer.setOpacity(0);
        }
      }
    },
    
    /**
     * Hide and remove the animation canvas overlay.
     */
    hideCanvasOverlay() {
      if (this.animationCanvas) {
        this.animationCanvas.remove();
        this.animationCanvas = null;
      }
    },
    
    /**
     * Display a specific canvas frame by index, optionally blended with next frame.
     * @param {number} frameIndex - Current frame index
     * @param {number} blendFactor - 0-1, how much to blend toward next frame (0 = current only)
     */
    showCanvasFrame(frameIndex, blendFactor = 0) {
      if (!this.animationCanvas || frameIndex < 0 || frameIndex >= this.canvasFrameCache.length) return;
      
      const frame = this.canvasFrameCache[frameIndex];
      const ctx = this.animationCanvas.getContext('2d');
      
      // Clear canvas
      ctx.clearRect(0, 0, this.animationCanvas.width, this.animationCanvas.height);
      
      // Use preloaded image if available, otherwise create new
      const currentImg = frame.img && frame.img.complete ? frame.img : null;
      
      if (!currentImg) {
        // Fallback: load image synchronously-ish
        const img = new Image();
        img.onload = () => {
          ctx.drawImage(img, 0, 0);
        };
        img.src = frame.imageData;
        this.currentTime = new Date(frame.timeKey);
        // Note: Don't call updateAllLegends() here - legends don't change during animation
        // and calling it on every frame causes network requests
        return;
      }
      
      // Draw current frame
      if (blendFactor <= 0 || !this.crossfadeEnabled) {
        // No blending needed
        ctx.drawImage(currentImg, 0, 0);
      } else {
        // Crossfade: blend current and next frame
        const nextIndex = (frameIndex + 1) % this.canvasFrameCache.length;
        const nextFrame = this.canvasFrameCache[nextIndex];
        const nextImg = nextFrame.img && nextFrame.img.complete ? nextFrame.img : null;
        
        if (nextImg) {
          // Draw current frame at reduced opacity
          ctx.globalAlpha = 1 - blendFactor;
          ctx.drawImage(currentImg, 0, 0);
          
          // Draw next frame at blend opacity
          ctx.globalAlpha = blendFactor;
          ctx.drawImage(nextImg, 0, 0);
          
          ctx.globalAlpha = 1;
        } else {
          // Next frame not ready, just draw current
          ctx.drawImage(currentImg, 0, 0);
        }
      }
      
      // Update currentTime to match the frame
      this.currentTime = new Date(frame.timeKey);
      // Note: Don't call updateAllLegends() here - legends don't change during animation
      // (they depend on element/model, not time) and calling it on every frame causes network requests
    },

    /**
     * Start the crossfade animation loop using requestAnimationFrame.
     */
    startCrossfadeLoop() {
      this.crossfadeProgress = 0;
      this.crossfadeLastTime = performance.now();
      
      const animate = (timestamp) => {
        if (!this.isPlaying) {
          this.animationFrameRequestId = null;
          return;
        }
        
        // Calculate time delta
        const deltaMs = timestamp - this.crossfadeLastTime;
        
        // Calculate frame duration based on playback speed (frames per second)
        const frameDurationMs = 1000 / this.playbackSpeed;
        
        // Update crossfade progress
        this.crossfadeProgress += deltaMs / frameDurationMs;
        
        // Check if we need to advance to next frame
        if (this.crossfadeProgress >= 1) {
          // Move to next frame
          this.animationFrameIndex = (this.animationFrameIndex + 1) % this.canvasFrameCache.length;
          this.crossfadeProgress = this.crossfadeProgress % 1; // Keep fractional part for smooth transition
        }
        
        // Only blend in the last 30% of the frame duration for sharper display
        // This keeps the frame sharp for most of its duration, then smoothly transitions
        const blendThreshold = 0.7;
        let blendFactor = 0;
        if (this.crossfadeProgress > blendThreshold) {
          // Normalize to 0-1 range for the blend portion, with ease-out curve
          const normalizedProgress = (this.crossfadeProgress - blendThreshold) / (1 - blendThreshold);
          blendFactor = normalizedProgress * normalizedProgress; // Quadratic ease-in for smoother start
        }
        this.showCanvasFrame(this.animationFrameIndex, blendFactor);
        
        this.crossfadeLastTime = timestamp;
        this.animationFrameRequestId = requestAnimationFrame(animate);
      };
      
      this.animationFrameRequestId = requestAnimationFrame(animate);
    },
    
    /**
     * Stop the crossfade animation loop.
     */
    stopCrossfadeLoop() {
      if (this.animationFrameRequestId) {
        cancelAnimationFrame(this.animationFrameRequestId);
        this.animationFrameRequestId = null;
      }
      this.crossfadeProgress = 0;
    },
    
    /**
     * Advance to the next canvas frame (legacy, for interval-based animation).
     */
    advanceCanvasFrame() {
      this.animationFrameIndex = (this.animationFrameIndex + 1) % this.canvasFrameCache.length;
      this.showCanvasFrame(this.animationFrameIndex, 0);
    },
    
    /**
     * Clear the canvas frame cache and release memory.
     */
    clearCanvasCache() {
      // Explicitly clear Image references to help garbage collection
      for (const frame of this.canvasFrameCache) {
        if (frame.img) {
          frame.img.src = ''; // Release the data URL reference
          frame.img = null;
        }
        frame.imageData = null;
      }
      this.canvasFrameCache = [];
    },
    
    // ========================================
    // End Canvas Animation Methods
    // ========================================

    increaseTimeSkip() {
      const currentIndex = this.timeSkipPresets.indexOf(this.manualTimeSkip);
      if (currentIndex < this.timeSkipPresets.length - 1) {
        this.manualTimeSkip = this.timeSkipPresets[currentIndex + 1];
      } else if (this.manualTimeSkip < 120) {
        this.manualTimeSkip = Math.min(120, this.manualTimeSkip + 30);
      }
    },
    
    decreaseTimeSkip() {
      const currentIndex = this.timeSkipPresets.indexOf(this.manualTimeSkip);
      if (currentIndex > 0) {
        this.manualTimeSkip = this.timeSkipPresets[currentIndex - 1];
      } else if (this.manualTimeSkip > 1) {
        this.manualTimeSkip = Math.max(1, this.manualTimeSkip - 1);
      }
    },
    
    incrementTime() {
      const newTime = this.calculateNewTime(this.currentTime, this.currentTimeSkip);
      this.currentTime = this.clampToLoopWindow(newTime, true);
      this.refreshAllLayers();
    },
    
    decrementTime() {
      const newTime = this.calculateNewTime(this.currentTime, -this.currentTimeSkip);
      this.currentTime = this.clampToLoopWindow(newTime, false);
      this.refreshAllLayers();
    },
    
    resetToInitialTime() {
      const now = this.getLastInterval();
      this.currentTime = this.clampToLoopWindow(now, true);
      this.refreshAllLayers();
    },
    
    calculateNewTime(time, deltaMinutes) {
      const newTime = new Date(time);
      newTime.setUTCMinutes(newTime.getUTCMinutes() + deltaMinutes);
      return newTime;
    },
    
    /**
     * For step-based loop window, manual time navigation is unrestricted.
     * Animation frames are generated relative to currentTime, so no clamping needed.
     * @param {Date} time - The time to return
     * @param {boolean} forward - Unused, kept for API compatibility
     * @returns {Date} - The same time, unchanged
     */
    clampToLoopWindow(time, forward) {
      return time;
    },
    
    getLastInterval() {
      const now = new Date();
      const minutes = Math.floor(now.getUTCMinutes() /  this.currentTimeSkip) *  this.currentTimeSkip;
      now.setUTCMinutes(minutes, 0, 0);
      return now;
    },
    
    /**
     * Reset the current time to now (rounded to current time skip interval)
     */
    resetToNow() {
      this.currentTime = this.getLastInterval();
      this.refreshAllLayers();
      // If animation is playing, restart to regenerate frames from new time
      this.restartAnimationIfPlaying();
    },
    
    onDateTimeInput(e) {
      this.tempDateTimeValue = e.target.value;
    },
    
    applyDateTime() {
      if (this.tempDateTimeValue) {
        const date = new Date(this.tempDateTimeValue + ':00Z');
        this.currentTime = date;
        this.refreshAllLayers();
      }
      this.showDateTimePicker = false;
    },
    
    formatFullDateTime(date) {
      const pad = n => String(n).padStart(2, '0');
      return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}T${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}:00`;
    },
    
    /**
     * Format a short time string (HH:mm) from an ISO date string.
     * Shows date prefix if different from today.
     */
    formatShortTime(isoString) {
      if (!isoString) return '';
      const date = new Date(isoString);
      const pad = n => String(n).padStart(2, '0');
      const hours = pad(date.getUTCHours());
      const minutes = pad(date.getUTCMinutes());
      
      // Check if it's the same day as current time
      const now = this.currentTime || new Date();
      const sameDay = date.getUTCFullYear() === now.getUTCFullYear() &&
                      date.getUTCMonth() === now.getUTCMonth() &&
                      date.getUTCDate() === now.getUTCDate();
      
      if (sameDay) {
        return `${hours}:${minutes}`;
      } else {
        // Include month/day for different days
        const month = pad(date.getUTCMonth() + 1);
        const day = pad(date.getUTCDate());
        return `${month}/${day} ${hours}:${minutes}`;
      }
    },
    
    /**
     * Seek to a specific frame in the animation.
     * Called when user drags the timeline slider.
     */
    seekToFrame(frameIndex) {
      if (frameIndex < 0 || frameIndex >= this.animationTimeKeys.length) return;
      
      this.animationFrameIndex = frameIndex;
      
      // Update currentTime to match the frame
      const timeKey = this.animationTimeKeys[frameIndex];
      if (timeKey) {
        this.currentTime = new Date(timeKey);
      }
      
      // Update display based on animation mode
      if (this.useCanvasAnimation && this.canvasFrameCache.length > frameIndex && this.animationCanvas) {
        // Canvas mode with valid frame - show it directly
        this.showCanvasFrame(frameIndex, 0);
      } else if (Object.keys(this.animationBuffers).length > 0) {
        // Tile layer mode with buffers - update the buffers
        this.updateBuffersForFrame(frameIndex);
      } else {
        // No animation system active - refresh the regular tile layers
        this.refreshAllLayers();
      }
    },
    
    /**
     * Called when user starts dragging the timeline.
     * Pauses animation during drag.
     */
    onTimelineDragStart() {
      this.isTimelineDragging = true;
      this.wasPlayingBeforeDrag = this.isPlaying;
      if (this.isPlaying) {
        this.pauseMovie();
      }
    },
    
    /**
     * Called when user stops dragging the timeline.
     * Resumes animation if it was playing before.
     */
    onTimelineDragEnd() {
      this.isTimelineDragging = false;
      if (this.wasPlayingBeforeDrag) {
        // Small delay to let the frame settle before resuming
        setTimeout(() => {
          if (!this.isPlaying && !this.isTimelineDragging) {
            this.resumeMovie();
          }
        }, 100);
      }
    },
    
    /**
     * Update tile layer buffers to show a specific frame.
     * Used when seeking via timeline slider in tile layer mode.
     */
    updateBuffersForFrame(frameIndex) {
      if (Object.keys(this.animationBuffers).length === 0) return;
      
      const timeKey = this.animationTimeKeys[frameIndex];
      if (!timeKey) return;
      
      const visibleLayers = this.activeLayers.filter(layer => layer.visible);
      
      for (const layer of visibleLayers) {
        const buffer = this.animationBuffers[layer.id];
        if (!buffer) continue;
        
        const newUrl = this.buildTileUrl(layer.element, layer.model, new Date(timeKey));
        
        // Update front buffer with new URL and show it
        buffer.front.setUrl(newUrl);
        buffer.front.setOpacity(buffer.targetOpacity);
        buffer.frontTimeKey = timeKey;
        
        // Update back buffer with next frame
        const nextIndex = (frameIndex + 1) % this.animationTimeKeys.length;
        const nextTimeKey = this.animationTimeKeys[nextIndex];
        const nextUrl = this.buildTileUrl(layer.element, layer.model, new Date(nextTimeKey));
        buffer.back.setUrl(nextUrl);
        buffer.back.setOpacity(0);
        buffer.backTimeKey = nextTimeKey;
      }
      
      this.nextFrameReady = true;
    },
    
    /**
     * Pause the movie without clearing state.
     */
    pauseMovie() {
      if (this.movieInterval) {
        clearInterval(this.movieInterval);
        this.movieInterval = null;
      }
      if (this.animationFrameRequestId) {
        cancelAnimationFrame(this.animationFrameRequestId);
        this.animationFrameRequestId = null;
      }
      this.isPlaying = false;
    },
    
    /**
     * Resume the movie from current frame.
     */
    resumeMovie() {
      if (this.isPlaying) return;
      
      this.isPlaying = true;
      
      if (this.useCanvasAnimation && this.canvasFrameCache.length > 0) {
        // Resume canvas animation
        this.startCrossfadeLoop();
      } else if (Object.keys(this.animationBuffers).length > 0) {
        // Resume tile layer animation
        this.movieInterval = setInterval(() => {
          this.advanceDoubleBufferFrame();
        }, 1000 / this.playbackSpeed);
      }
    },
    
    /**
     * Update view info with throttling to prevent excessive updates during rapid pan/zoom.
     * Uses trailing edge throttle - updates at end of rapid movements.
     */
    updateViewInfo() {
      if (!this.map) return;
      
      // Throttle: cancel pending update, schedule new one
      if (this.updateViewInfoTimer) {
        clearTimeout(this.updateViewInfoTimer);
      }
      
      this.updateViewInfoTimer = setTimeout(() => {
        if (!this.map) return;
        const c = this.map.getCenter();
        this.viewInfo.lat = Number(c.lat.toFixed(5));
        this.viewInfo.lng = Number(c.lng.toFixed(5));
        this.viewInfo.zoom = this.map.getZoom();
        this.viewInfo.centerPair = `${this.viewInfo.lat},${this.viewInfo.lng}`;
        this.updateViewInfoTimer = null;
      }, 100); // 100ms throttle
    },
    
    async copyCenterToClipboard() {
      try {
        await navigator.clipboard.writeText(this.viewInfo.centerPair);
      } catch (e) {
        console.warn('Copy failed', e);
      }
    },
    
    getLayerUrlPattern(layer) {
      const sourceParam = layer.model && layer.model !== 'default' ? `&source=${layer.model}` : '';
      const strictParam = this.strictMode ? '' : '&strict=false';
      // Use actual key if custom, otherwise show placeholder
      const keyValue = this.hasCustomApiKey ? this.weatherApiKey : 'YOUR_API_KEY';
      return `${this.baseApiUrl}/VisualCrossingWebServices/rest/api/v1/map/tile/` +
             `${layer.element}/{z}/{x}/{y}.webp` +
             `?key=${keyValue}` +
             `&time={time}` +
             `&unitGroup=${this.unitGroup}` +
             `${strictParam}` +
             `${sourceParam}`;
    },
    
    getLegendUrlPattern(layer) {
      // Use actual key if custom, otherwise show placeholder
      const keyValue = this.hasCustomApiKey ? this.weatherApiKey : 'YOUR_API_KEY';
      return `${this.baseApiUrl}/VisualCrossingWebServices/rest/api/v1/map/legend/` +
             `${layer.element}?key=${keyValue}&unitGroup=${this.unitGroup}`;
    },
    
    async copyUrlPattern(layer, type = 'tile') {
      try {
        const url = type === 'legend' 
          ? this.getLegendUrlPattern(layer) 
          : this.getLayerUrlPattern(layer);
        await navigator.clipboard.writeText(url);
      } catch (e) {
        console.warn('Copy failed', e);
      }
    },
    
    getColor(value, element) {
      if (this.elementBinCache[element]) {
        const bins = this.elementBinCache[element];
        for (let bin of bins) {
          if (value >= bin.min && (bin.max == null || value < bin.max)) {
            return bin.color;
          }
        }
      }
      return '#CFCFCF';
    },
    
   getContrastingTextColor(hex) {
      if (!hex) return '#000000';
      const h = hex.replace('#', '');
      const r = parseInt(h.slice(0, 2), 16);
      const g = parseInt(h.slice(2, 4), 16);
      const b = parseInt(h.slice(4, 6), 16);
      const L = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      return L > 0.5 ? '#000000' : '#FFFFFF';
    },
    
    saveSettings() {
      try {
        const settings = {
          unitGroup: this.unitGroup,
          baseApiUrl: this.baseApiUrl,
          selectedBaseMap: this.selectedBaseMap,
          showLabels: this.showLabels,
          showGeoJson: this.showGeoJson,
          geoJsonElement: this.geoJsonElement,
          strictMode: this.strictMode,
          playbackSpeed: this.playbackSpeed,
          autoTimeSkip: this.autoTimeSkip,
          manualTimeSkip: this.manualTimeSkip,
          apiKeyOverride: this.apiKeyOverride,
          activeLayers: this.activeLayers.map(layer => ({
            id: layer.id,
            model: layer.model,
            element: layer.element,
            visible: layer.visible,
            opacity: layer.opacity
          })),
          nextLayerId: this.nextLayerId
        };
        localStorage.setItem('weatherMapSettings', JSON.stringify(settings));
      } catch (e) {
        console.warn('Failed to save settings:', e);
      }
    },
    
    loadSettings() {
      try {
        const saved = localStorage.getItem('weatherMapSettings');
        if (!saved) return;
        
        const settings = JSON.parse(saved);
        
        if (settings.unitGroup) this.unitGroup = settings.unitGroup;
        if (settings.baseApiUrl) this.baseApiUrl = settings.baseApiUrl;
        if (settings.selectedBaseMap) this.selectedBaseMap = settings.selectedBaseMap;
        if (typeof settings.showLabels === 'boolean') this.showLabels = settings.showLabels;
        if (typeof settings.showGeoJson === 'boolean') this.showGeoJson = settings.showGeoJson;
        if (settings.geoJsonElement) this.geoJsonElement = settings.geoJsonElement;
        if (typeof settings.strictMode === 'boolean') this.strictMode = settings.strictMode;
        if (settings.playbackSpeed) this.playbackSpeed = settings.playbackSpeed;
        if (typeof settings.autoTimeSkip === 'boolean') this.autoTimeSkip = settings.autoTimeSkip;
        if (settings.manualTimeSkip) this.manualTimeSkip = settings.manualTimeSkip;
        if (settings.nextLayerId) this.nextLayerId = settings.nextLayerId;
        if (settings.apiKeyOverride) this.apiKeyOverride = settings.apiKeyOverride;
        
        if (Array.isArray(settings.activeLayers)) {
          this.activeLayers = settings.activeLayers.map(layer => ({
            ...layer,
            leafletLayer: null // Will be created after map init
          }));
        }
      } catch (e) {
        console.warn('Failed to load settings:', e);
      }
    },
    
    /**
     * Handle API key override changes
     */
    onApiKeyChange() {
      this.saveSettings();
      this.tileError = null; // Clear any existing error
      this.refreshAllLayers();
      if (this.showGeoJson) {
        this.refreshGeoJsonTiles();
      }
    },
    
    /**
     * Clear API key override and use session key
     */
    clearApiKeyOverride() {
      this.apiKeyOverride = '';
      this.saveSettings();
      this.tileError = null;
      this.refreshAllLayers();
      if (this.showGeoJson) {
        this.refreshGeoJsonTiles();
      }
    },
    
    /**
     * Handle tile loading errors
     * Shows error in UI for API key related issues
     */
    handleTileError(error, layer) {
      // Increment error counts
      this.tileErrorCount++;
      if (!this.layerErrors[layer.id]) {
        this.layerErrors[layer.id] = 0;
      }
      this.layerErrors[layer.id]++;
      
      // Clear any existing timeout for the message
      if (this.tileErrorTimeout) {
        clearTimeout(this.tileErrorTimeout);
      }
      
      // Try to get more info about the error
      const tile = error.tile;
      const url = tile?.src || '';
      
      // Check if it's likely an API key issue (401/403 errors)
      // We can't directly access HTTP status, but we can infer from the URL
      if (url.includes('key=')) {
        this.tileError = 'Failed to load map tiles. This may be due to an invalid or expired API key. ' +
                        'Check the API Integration section to set your API key.';
      } else {
        this.tileError = 'Failed to load map tiles. Please check your connection and try again.';
      }
      
      // Don't auto-dismiss if we have many errors - keep showing until user dismisses
      if (this.tileErrorCount < 5) {
        this.tileErrorTimeout = setTimeout(() => {
          this.tileError = null;
        }, 15000);
      }
    },
    
    /**
     * Manually dismiss the tile error banner
     */
    dismissTileError() {
      this.tileError = null;
      this.tileErrorCount = 0;
      this.layerErrors = {};
      if (this.tileErrorTimeout) {
        clearTimeout(this.tileErrorTimeout);
        this.tileErrorTimeout = null;
      }
    }
  }
};
</script>

<style scoped>
.weather-map-container {
  position: relative;
  width: 100%;
  height: 100vh;
  overflow: hidden;
}

/* Spinning animation for loading indicator */
@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.spin {
  animation: spin 1s linear infinite;
}

/* Animation preload overlay */
.preload-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1200;
  display: flex;
  align-items: center;
  justify-content: center;
}

.preload-content {
  background: white;
  border-radius: 12px;
  padding: 24px 32px;
  text-align: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  min-width: 200px;
}

.preload-content svg {
  color: #0d6efd;
  margin-bottom: 12px;
}

.preload-text {
  font-size: 14px;
  color: #333;
  margin-bottom: 12px;
}

.preload-progress {
  width: 100%;
  height: 6px;
  background: #e9ecef;
  border-radius: 3px;
  overflow: hidden;
  margin-bottom: 8px;
}

.preload-progress-bar {
  height: 100%;
  background: #0d6efd;
  border-radius: 3px;
  transition: width 0.2s ease-out;
}

.preload-percent {
  font-size: 12px;
  color: #666;
}

/* Animation Control Bar */
.animation-control-bar {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  gap: 0;
  background: white;
  padding: 6px 10px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
}

.control-bar-main {
  display: flex;
  align-items: center;
  gap: 4px;
}

.control-bar-timeline {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-top: 6px;
  margin-top: 6px;
  border-top: 1px solid #e0e0e0;
  min-width: 280px;
}

.time-display-inline {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  color: #333;
  transition: background 0.15s;
}

.time-display-inline:hover {
  background: #f0f0f0;
}

.time-display-inline svg {
  opacity: 0.6;
}

.time-text {
  font-family: monospace;
  font-size: 11px;
}

.playback-buttons {
  display: flex;
  align-items: center;
  gap: 2px;
  margin-left: 4px;
  padding-left: 8px;
  border-left: 1px solid #e0e0e0;
}

.anim-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  border-radius: 4px;
  cursor: pointer;
  color: #555;
  transition: all 0.15s;
}

.anim-btn:hover:not(:disabled) {
  background: #f0f0f0;
  color: #333;
}

.anim-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.anim-btn.play-btn {
  width: 32px;
  height: 32px;
  background: #0d6efd;
  color: white;
  border-radius: 50%;
}

.anim-btn.play-btn:hover:not(:disabled) {
  background: #0b5ed7;
  color: white;
}

.anim-btn.settings-btn {
  margin-left: 2px;
}

.anim-btn.settings-btn.active,
.anim-btn.expand-btn.active {
  background: #e7f1ff;
  color: #0d6efd;
}

/* Reset to now button (separate) */
.reset-now-btn-separate {
  color: #28a745;
  margin-left: 2px;
}

.reset-now-btn-separate:hover {
  background: #d4edda;
  color: #28a745;
}

/* Timeline Scrubber (legacy, now used in panel) */
.timeline-scrubber {
  position: absolute;
  top: 52px;
  right: 10px;
  z-index: 999;
  display: flex;
  align-items: center;
  gap: 8px;
  background: white;
  padding: 6px 12px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
  min-width: 280px;
}

.timeline-label {
  font-size: 10px;
  font-family: monospace;
  color: #666;
  white-space: nowrap;
  min-width: 40px;
}

.timeline-label.start {
  text-align: right;
}

.timeline-label.end {
  text-align: left;
}

.timeline-slider-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 150px;
}

.timeline-slider {
  width: 100%;
  height: 6px;
  -webkit-appearance: none;
  appearance: none;
  background: #e0e0e0;
  border-radius: 3px;
  outline: none;
  cursor: pointer;
}

.timeline-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 14px;
  height: 14px;
  background: #0d6efd;
  border-radius: 50%;
  cursor: grab;
  box-shadow: 0 1px 3px rgba(0,0,0,0.3);
  transition: transform 0.1s;
}

.timeline-slider::-webkit-slider-thumb:hover {
  transform: scale(1.2);
}

.timeline-slider::-webkit-slider-thumb:active {
  cursor: grabbing;
  transform: scale(1.1);
}

.timeline-slider::-moz-range-thumb {
  width: 14px;
  height: 14px;
  background: #0d6efd;
  border: none;
  border-radius: 50%;
  cursor: grab;
  box-shadow: 0 1px 3px rgba(0,0,0,0.3);
}

.timeline-slider::-moz-range-thumb:active {
  cursor: grabbing;
}

.timeline-current-time {
  font-size: 10px;
  font-family: monospace;
  color: #0d6efd;
  font-weight: 600;
  margin-top: 2px;
}

/* Animation Settings Panel */
.animation-settings-panel {
  position: absolute;
  top: 54px;
  right: 10px;
  z-index: 1000;
  width: 280px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.15);
  padding: 12px;
}

.settings-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #eee;
}

.settings-header strong {
  font-size: 13px;
}

.close-btn {
  background: none;
  border: none;
  font-size: 18px;
  color: #999;
  cursor: pointer;
  padding: 0;
  line-height: 1;
}

.close-btn:hover {
  color: #333;
}

.setting-row {
  margin-bottom: 12px;
}

.setting-row:last-child {
  margin-bottom: 0;
}

.setting-row label {
  display: block;
  font-size: 12px;
  font-weight: 500;
  color: #555;
  margin-bottom: 4px;
}

.setting-row .form-range {
  width: 100%;
}

.loop-window-display {
  font-size: 13px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
  padding: 6px 10px;
  background: #f8f9fa;
  border-radius: 4px;
  text-align: center;
}

.slider-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}

.slider-label {
  font-size: 11px;
  color: #666;
  width: 35px;
  flex-shrink: 0;
}

.slider-value {
  font-size: 11px;
  color: #333;
  width: 45px;
  text-align: right;
  flex-shrink: 0;
}

/* Tile error banner styling */
.tile-error-banner {
  position: absolute;
  top: 10px;
  right: 10px;
  left: auto;
  z-index: 1100;
  max-width: 400px;
  margin: 0;
  padding: 10px 15px;
  font-size: 13px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.2);
}

.tile-error-banner strong {
  display: inline;
}

/* Layer error badge indicator */
.layer-error-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  background: #dc3545;
  color: white;
  border-radius: 50%;
  font-size: 10px;
  font-weight: bold;
  flex-shrink: 0;
}

/* API key warning in layer list */
.api-key-warning {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 8px;
  margin-bottom: 4px;
  background: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 4px;
  color: #856404;
  font-size: 11px;
}

.api-key-warning svg {
  flex-shrink: 0;
  color: #856404;
}

/* Model badge in layer list */
.model-badge {
  display: inline-block;
  background: #6c757d;
  color: white;
  font-size: 9px;
  padding: 1px 4px;
  border-radius: 3px;
  margin-left: 4px;
  vertical-align: middle;
  font-weight: 500;
}

/* Marker list styles */
.marker-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 6px;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.marker-item:hover {
  background-color: #f0f0f0;
}

.marker-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}

.marker-coords {
  flex-grow: 1;
  font-size: 12px;
  color: #333;
}

.marker-remove-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  padding: 0;
  margin-left: 4px;
  background: none;
  border: none;
  color: #dc3545;
  font-size: 16px;
  font-weight: bold;
  line-height: 1;
  cursor: pointer;
  border-radius: 3px;
  flex-shrink: 0;
}

.marker-remove-btn:hover {
  background-color: #fee2e2;
}

.map-view {
  width: 100%;
  height: 100%;
}

.map-controls {
  position: absolute;
  top: 10px;
  left: 50px;
  z-index: 1000;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.2);
  max-width: 300px;
  transition: transform 0.1s ease, opacity 0.1s ease;
}

/* Hide controls when collapsed on desktop */
.map-controls:not(.controls-open) {
  transform: translateX(-110%);
  opacity: 0;
  pointer-events: none;
}

.map-controls.controls-open {
  transform: translateX(0);
  opacity: 1;
  pointer-events: auto;
}

.controls-toggle {
  display: none;
  position: absolute;
  top: 10px;
  left: 10px;
  width: 40px;
  height: 40px;
  background: white;
  border: none;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.2);
  font-size: 20px;
  z-index: 1001;
  cursor: pointer;
}





.controls-panel {
  padding: 15px;
  max-height: calc(100vh - 100px);
  overflow-y: auto;
}

.control-group {
  margin-bottom: 15px;
}

.control-group label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 5px;
  color: #333;
}
.control-row {
  display: flex;
  gap: 10px;
  margin-bottom: 10px;
}

.control-row .control-group {
  margin-bottom: 0;
}

.control-group.compact {
  margin-bottom: 8px;
}

.control-group.compact label {
  margin-bottom: 3px;
}
.layer-list {
  max-height: 250px;
  overflow-y: auto;
}

.layer-item {
  padding: 8px 0;
  border-bottom: 1px solid #eee;
}

.layer-item:last-child {
  border-bottom: none;
}

.layer-controls {
  padding-left: 28px;
  margin-top: 5px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.form-range-sm {
  height: 0.5rem;
}

.time-display {
  position: absolute;
  top: 10px;
  right: 10px;
  background: rgba(255, 255, 255, 0.95);
  padding: 8px 15px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
  cursor: pointer;
  z-index: 1000;
  display: flex;
  align-items: center;
  gap: 6px;
}

.time-display:hover {
  background: white;
}

.datetime-picker-modal,
.add-layer-modal {
  position: fixed;  /* Changed from absolute to fixed */
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.modal-content-custom {
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.3);
  min-width: 280px;
  max-width: 90vw;
  position: relative;  /* Ensure content is above backdrop */
  z-index: 2001;
}

.modal-content-custom h6 {
  margin-bottom: 15px;
  font-weight: 600;
}

.advanced-header {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 4px 0;
}

.advanced-header:hover {
  opacity: 0.8;
}

.advanced-content {
  margin-top: 8px;
  padding: 8px;
  background: #f8f9fa;
  border-radius: 6px;
}

.url-pattern-code {
  display: block;
  padding: 8px;
  background: #2d3748;
  color: #e2e8f0;
  border-radius: 4px;
  font-size: 11px;
  word-break: break-all;
  white-space: pre-wrap;
  line-height: 1.4;
}

.url-pattern-item {
  border-bottom: 1px solid #e2e8f0;
  padding-bottom: 8px;
}

.url-pattern-item:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.legend-container {
  position: absolute;
  bottom: 20px;
  right: 20px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.2);
  max-width: 320px;
  z-index: 1000;
}

.legend-header {
  padding: 10px 15px;
  background: #f8f9fa;
  border-radius: 8px 8px 0 0;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
}

.legend-collapsed .legend-header {
  border-radius: 8px;
}

.legend-content {
  padding: 5px 0;
  max-height: 300px;
  overflow-y: auto;
}

.legend-item {
  border-bottom: 1px solid #eee;
}

.legend-item:last-child {
  border-bottom: none;
}

.legend-item-header {
  padding: 8px 15px;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.legend-item-header:hover {
  background: #f8f9fa;
}

.legend-item-title {
  font-size: 12px;
  font-weight: 500;
}

.legend-item-content {
  padding: 5px 15px 10px 15px;
}
.time-skip-control {
  background: #f8f9fa;
  border-radius: 6px;
  padding: 8px;
}

.time-skip-display {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.time-skip-display.auto-mode {
  opacity: 0.7;
}

.time-skip-value {
  display: flex;
  align-items: baseline;
  gap: 2px;
  min-width: 60px;
  justify-content: center;
}

.skip-number {
  font-size: 18px;
  font-weight: 700;
  color: #333;
}
.controls-panel::-webkit-scrollbar,
.layer-list::-webkit-scrollbar,
.legend-content::-webkit-scrollbar {
  width: 6px;
}

.controls-panel::-webkit-scrollbar-track,
.layer-list::-webkit-scrollbar-track,
.legend-content::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.controls-panel::-webkit-scrollbar-thumb,
.layer-list::-webkit-scrollbar-thumb,
.legend-content::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 3px;
}

@media (max-width: 768px) {
  .controls-toggle {
    display: block;
  }
  
  .map-controls {
    top: 60px;
    left: 10px;
    max-width: 280px;
    z-index: 1100;  /* Lower z-index so modals appear above */
  }
  
  .layers-quick-toggle {
    display: none;
  }
  
  .controls-panel {
    max-height: calc(100vh - 20px);
  }
  
  .time-display {
    font-size: 12px;
    padding: 6px 10px;
  }
  
  .animation-control-bar {
    padding: 4px 8px;
  }
  
  .time-text {
    font-size: 10px;
  }
  
  .anim-btn {
    width: 24px;
    height: 24px;
  }
  
  .anim-btn.play-btn {
    width: 28px;
    height: 28px;
  }
  
  .animation-settings-panel {
    width: 260px;
    right: 5px;
  }
  
  .legend-container {
    bottom: 10px;
    right: 10px;
    max-width: 200px;
  }
  
  .legend-content {
    max-height: 150px;
  }
}

@media (max-width: 480px) {
  .map-controls {
    max-width: calc(100vw - 20px);
  }
  
  .time-display {
    right: 60px;
  }
  
  .animation-control-bar {
    right: 5px;
    left: auto;
    max-width: calc(100vw - 60px);
  }
  
  .time-display-inline {
    padding: 2px 4px;
  }
  
  .time-text {
    max-width: 120px;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  
  .animation-settings-panel {
    width: calc(100vw - 20px);
    right: 10px;
    left: 10px;
  }
}
</style>


<style>
/* Leaflet layers control button - not scoped */
.layers-control-btn {
  width: 30px !important;
  height: 30px !important;
  display: flex !important;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  color: #333;
}

.layers-control-btn:hover {
  background-color: #f4f4f4;
  color: #333;
}

.layers-control-btn svg {
  width: 16px;
  height: 16px;
}
.city-marker-popup .leaflet-popup-content-wrapper {
  padding: 4px 8px;
  border-radius: 6px;
}

.city-marker-popup .leaflet-popup-content {
  margin: 6px;
  font-size: 12px;
}

.city-marker-popup .leaflet-popup-tip-container {
  display: none;
}
</style>